import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { disposeChildren, disposeHammer, disposeResizeObserver, setDisposable } from "@cervval.js/client-html5-core/src/components/disposable";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, setDisplay, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, FLEX_CONTAINER, FLEX_CONTAINER_CENTERED, FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, H_100, JUSTIFY_CONTENT, OVERFLOW, PADDING, POSITION, TEXT_TRUNCATE, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, KeyboardInput, REF_ATTRIBUTE, appendHTMLElementContent, createHtmlElement, empty, ignoreResizeObserverLoopLimit, isScrollable, setHTMLElementStyle, setHtmlElementOptions } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { DomTools } from "@cervval.js/client-html5-core/tools";
import { Action } from "@cervval.js/common-action/src/action.model";
import { EncapsulateInActionWithoutParams } from "@cervval.js/common-action/src/services/action.service";
import { DATA_SOURCE_SERVICE } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { PropertyConfig, PropertyOptionsDuration, PropertyOptionsList } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { UTC } from "@cervval.js/common/src/models/locale.model";
import { DefaultDataSourceService } from "@cervval.js/common/src/services/dataSourceService.impl/defaultDataSourceService";
import { LOG_SERVICE, LogService } from "@cervval.js/common/src/services/log.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { destroyObject } from "@cervval.js/common/src/tools/cache.tools";
import { addToArrayRecord, buildObjectMap, indexOf, wrapAsArray } from "@cervval.js/common/src/tools/collection.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { ValueOrFunction, anyReallyAny, getVOFValue, is, sortFields } from "@cervval.js/common/src/tools/object.tools";
import { formatMinutesHHMM, formatSecondsHHMM } from "@cervval.js/common/src/tools/time.tools";
import { DEFAULT_DATETIME_FORMAT, DEFAULT_DATE_FORMAT, format, formatFinishDate, getDefaultDateLocale, toDate, toJsDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { ColumnUid, RowUid, SmartTableColumnConfig } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { RenderCacheData } from "@cervval.js/common/src/ui/renderCacheData.types";
import fastdom from "fastdom/fastdom";
import Hammer from "hammerjs";
import { Tooltip } from "../../bootstrap";
import { CjsCheckboxEditor } from "../../editors/checkboxEditor/checkboxEditor";
import { CjsEditor } from "../../editors/editor";
import { EDITOR_FACTORY_SERVICE, EditorFactoryService } from "../../editors/editorFactory.service";
import { DragRef } from "../../services/dragAndDrop/dragRef";
import { ButtonStyle, CjsButton } from "../button/button";
import { CjsCheckbox } from "../checkbox/checkbox";
import { CjsEllipsis } from "../ellipsis/ellipsis";
import { CjsInput, InputOptions } from "../inputs/input";
import { CjsSmartScrollBar } from "../smartScrollBar/smartScrollBar";
import { ColumnConfiguratorOptions } from "./columnConfigurator/columnConfigurator";
import { I18nType, i18n } from "./i18n/i18n";
import { SELECTION_TYPE, SelectionChange, SmartTableSelectionModel } from "./selectionModel/smartTableSelectionModel";
import { HEADER_CHECKBOX_INPUT } from "./smartTableCheckboxSelectorColumn";
import { SmartTableColumn, addStickyColumn, checkColumnDuplicatesUid, createSmartTableResizerColumn, getColumnsFromConfiguration, getFirstColumn, getSmartTableColumnAlignmentClass, getSmartTableColumnAlignmentPaddingClass, getSmartTableColumnWidth, getVisibleColumnsCount, getVisibleColumnsFromConfiguration, isCheckboxColumn, isSmartTableColumnResizable } from "./smartTableColumn.tools";
import { SmartTableDragAndDropHelper, SmartTableDragAndDropOptions } from "./smartTableDragAndDrop.helper";
import { SmartTableEditionHelper } from "./smartTableEdition.helper";
import { SmartTableFilterHelper, SmartTableFilterOptions, SmartTableOnFilterEvent } from "./smartTableFilter.helper";
import { SMART_TABLE_HIERARCHY_DEFAULT_WIDTH_ICON, SmartTableCollapseButton, SmartTableCollapseButtonType, SmartTableCollapseIcons, SmartTableHierarchyHelper, SmartTableHierarchyInfo, SmartTableHierarchyOptions } from "./smartTableHierarchy.helper";
import { SmartTableSearchHelper } from "./smartTableSearch.helper";
import { ColumnSorted, SmartTableSortHelper } from "./smartTableSort.helper";
import { SmartTableBounds, SmartTableDisplayOptions, SmartTableStyleHelper, TABLE_HOVER, defaultSmartTableDisplayOptions } from "./smartTableStyle.helper";

export { RenderCacheData } from "@cervval.js/common/src/ui/renderCacheData.types";
/** @deprecated Use the function directly in tools, only here for keeping old stuff running  */
export { SmartTableColumn, smartTablePropertyColumn } from "./smartTableColumn.tools";


export type SmartTableCellEditorGetter<ItemData> = (cell: SmartTableCell<ItemData>) => CjsEditor<anyReallyAny> | undefined;
export type SmartTableCellStyleGetter<ItemData> = (cell: SmartTableCell<ItemData>, renderCacheData?: RenderCacheData) => CellTableStyle | undefined;
export type SmartTableCellRenderer<ItemData> = (cell: Omit<SmartTableCell<ItemData>, "stringValue">, renderCacheData?: RenderCacheData) => AcceptedHtmlElementContent | null;
export type SmartTableIsCellEditable<ItemData> = (cell: SmartTableCell<ItemData>) => boolean;

export type SmartTableOptions<ItemData> = SmartTableDisplayOptions<ItemData> & SmartTableFilterOptions & {

    /** Number that represents the element index displayed at the top of the smartTable */
    firstDisplayedItemIndex: number,

    /** Number that represents the height of 1 element of the smartTable in px */
    rowHeight: number | ((item: ItemData | null) => number),

    /** if true, rows will be added to the table even if there is no more room detected (=> you are telling the smartTable it should NOT be 'smart')*/
    parentContainerHasNoHeightConstraint?: boolean,

    /** scrollBar  - when used in timeline, this option must be set BEFORE init is called */
    scrollBar: "smart" | "standard",

    /** 
     * when scrollBar="smart" if this option is set, user can scroll until firstDisplayedItemIndex = last row (meaning there will be only one row displayed)
     * this is useful when using TABLE_NO_HEIGHT_CONSTRAINT_ON_TD_CLASS
     */
    allowScrollUntilLastRow?: boolean,

    /** To be used with scrollBar:standard, adjusts the cell padding top in order to always display the top of the first line (to be set before init)*/
    firstLineStickyContent?: boolean,

    /** Grow or shrink list item to fill and fit the list container */
    fitContent: boolean,

    /** Columns of smartTable */
    columns: SmartTableColumn<ItemData>[],

    /** Columns configuration */
    columnsConfig?: SmartTableColumnConfig[],

    /** Disable sticky rows resize for all rows of only for footer or header*/
    disableStickyRowsResize?: boolean | "footer" | "header",

    /** Height of resizer for sticky rows (in px) */
    stickyRowResizerHeight: number,

    /** Sticky row min height (in px) */
    stickyRowMinHeight: number | ((row: SmartTableRow<ItemData>) => number),

    /** Selection context for smartTable */
    selection?: SmartTableSelectionContext<ItemData>,

    /** 
     * Allows to override the copy function from table to same table, doesn't affect paste. You should re-implement your paste callback
     * FIXME Delete this option, see with OPO project
     */
    overrideCopy?: boolean,

    /** Define editable state of each cell - if not set no cell is editable */
    isCellEditable?: SmartTableIsCellEditable<ItemData>,

    /** If defined, overrides editor.setValue */
    setCellEditorValue?: (cell: SmartTableCell<ItemData>, editor: CjsEditor<anyReallyAny>, value: anyReallyAny) => void,

    /** If defined, edits are encapsulated within an action */
    encapsulateEditsInAction?: EncapsulateInActionWithoutParams,

    /** Define renderer of cells */
    renderCell?: SmartTableCellRenderer<ItemData>,

    /** 
     * If disposeCell is set, it will be called on cell cache clear 
     * This is useful when events listeners are installed on the cell in order to prevent memory leaks
     */
    disposeCell?: (cell: SmartTableCell<ItemData>) => void,

    /** 
     * Override populate row cells for specific rows
     * @returns true if row was populated
     */
    populateRowCells?: (rowTr: HTMLTableRowElement, row: SmartTableRow<ItemData>, params: { visibleColumns: SmartTableColumn<ItemData>[], renderCacheData: RenderCacheData }) => boolean,

    /** Customize row before it is appended to the DOM */
    customizeRowBeforeAppend?: (row: SmartTableRow<ItemData>) => void,

    /** Customize row after it is appended to the DOM */
    customizeRowAfterAppend?: (row: SmartTableRow<ItemData>) => void,

    /** 
     * Define editor for cell
     * You should not call this function directly, you must use smartTable.getCellEditor to get the editor of a cell (because it could be overridden by column)
     */
    getCellEditor?: SmartTableCellEditorGetter<ItemData>,

    /** CSS style to apply on cell */
    getCellStyle?: SmartTableCellStyleGetter<ItemData>,

    /** If set to true, several options are set on init (doNotObserveResize, autoScrollUpDisabled, rowHeightDrivenExternally) */
    withinTimeline?: boolean,

    /** 
     * Observe resize is done by default but some components (like the Timeline) may have a resize observer that refreshes the table
     * Cannot be changed after init
     */
    doNotObserveResize?: boolean,

    /** If true, row heights are updated on each render */
    rowHeightDrivenExternally?: boolean,

    /** 
     * By default, if the firstDisplayedItemIndex is too high, it will be adjusted during render in order to display a maximum of rows
     */
    autoScrollUpDisabled?: boolean,

    /** Hierarchy options */
    hierarchyOptions?: SmartTableHierarchyOptions<ItemData>,

    /** 
     * If true, we won't be able to resize columns with the mouse. Will be override for a specific column in SmartTableColumn config
     * ! Resizable column won't work properly if 'columnsLayout' option is "auto", use "fixed" !
     */
    columnsResizeDisabled?: boolean,

    /** Min-width(in px) of columns for their resize, default is 10px */
    minColumnResizeWidth: number,

    /**
     * used by SmartTableSortHelper if true null values are first when sorting in ascending order, otherwise they are last
     */
    sortNullFirst?: boolean,

    /** Get row uid from itemData, if not defined, row global index is rowUid */
    getRowUid?: (item: ItemData, globalIndex: RowGlobalIndex) => RowUid,

    /** 
     * Enable a columnConfigurator on smartTable */
    columnConfigurator?: {
        /** Enable column configurator */
        enable?: boolean,
        /** Column configurator options */
        options?: ColumnConfiguratorOptions
    },

    /** Drag and drop options for smartTable */
    dragAndDrop?: SmartTableDragAndDropOptions<ItemData>,

    /**
     * If false, table will not be 'smart'.
     * Rendered as classic html table, all rows are rendered on each refresh
     * ! options.firstDisplayedItemIndex value is always "0" in this mode
     */
    smart?: boolean,

    /** 
     * Enable search on smartTable
     * */
    search?: {
        /** Enable search */
        enable?: boolean,

        /** {true} will disable highlight of cells matching with current search */
        doNotHighlightSearch?: boolean
    }
};

export interface SmartTableSelectionContext<ItemData> {

    /** Enable selection display (add selection effect, toggle background color) */
    enableBackgroundDisplay?: boolean | ((row: SmartTableRow<ItemData>) => boolean);

    /** List selection model used by smartTable */
    selectionType?: SELECTION_TYPE;

    /** If true clicking on a row toggles the selection */
    toggleSelectionOnClick?: boolean | ((data: ItemData) => boolean);

    /** If returns true, row is selectable, else selection will have no effects */
    isRowSelectable?: (row: SmartTableRow<ItemData>, data: ItemData) => boolean;

    /**
     * If true, a parent row selection will also select children rows
     * * Only applied if hierarchyOptions is defined
     */
    hierarchyParentSelectionWillSelectChildren?: boolean;

}

/* Constant */
export const DEFAULT_SMART_TABLE_ROW_HEIGHT_VALUE = 45;

export const REFRESH_FILTER = "refreshFilter";
/** this const is used as the columnUid for single column tables (ex SmartList) */
export const SINGLE_COLUMN_UID = "@singleColumn";
export const COLUMN_UID_ATTRIBUTE = "col";
// Css classes
export const ROW_UID_ATTR = "row";
export const HIGHLIGHT_TABLE_ROW_SELECTED = "highlight-table-row-selected";
export const TABLE_ROW_SELECTED = "table-row-selected";
export const CELL_CONTENT = "cell-content";
export const CELL_FLEX = "cell-flex";
export const CELL_HIERARCHICAL = "cell-hierarchical";
const COLLAPSE_BTN = "collapse-btn";
export const SEARCH_FILTER = "search-filter";
const RESIZED_ROW_CLASS = "in-resize";
export const EMPTY_ROW_CLASS = "empty-row";
export const ROW_HEADER_CLASS = "row-header";
export const FILTERED_COLUMN = "filtered-column";
const TABLE_HEADER_CLASS = "table-header";
const TABLE_FOOTER_CLASS = "table-footer";
const FILTERABLE_HEADER_CLASS = "filterable-header";
const COLOR_CELL_CLASS = "color-cell";
const CLICKABLE_ROW_CLASS = "clickable-row";
const SORTABLE_HEADER_CLASS = "sortable-header";
const DISPLAY_SORT_NONE = "display-sort-none";
const INVERT_STRIPES_CLASS = "first-row-is-odd";
const STICKY_ROW_CLASS = "sticky-row";
const SCROLL_SHADOW_TOP_CLASS = "scroll-shadow-top";
const SCROLL_SHADOW_BOTTOM_CLASS = "scroll-shadow-bottom";
const HORIZONTAL_OVERFLOW_CLASS = "horizontal-overflow";
export const EXPANDED_TABLE_ROW = "expanded-table-row";
export const TABLE_FOOTER_INFO_CELL = "table-footer-info-cell";
export const TABLE_FOOTER_INFO_DIV = "table-footer-info-div";
export const CELL_CONTENT_STRING = "smart-table-cell-content-string";
export const CELL_CONTENT_HTML = "smart-table-cell-content-html";
export const TABLE_NOT_SMART_CLASS = "table-not-smart";
/** use this class if you want cells to automatically adjust their height regardless the row height */
export const TABLE_NO_HEIGHT_CONSTRAINT_ON_TD_CLASS = "table-no-height-constraint-on-tds";

// fill column is added after last column and is width 100%
export const FILL_COLUMN_UID = "__fill__";
const _fillColumn: SmartTableColumn<anyReallyAny> = {
    uid: FILL_COLUMN_UID, filterDisabled: true, sortDisabled: true,
    renderCell: () => ""
};

export const defaultSmartTableOptions: SmartTableOptions<unknown> = {

    /** Number that represents the element index displayed at the top of the smartTable */
    firstDisplayedItemIndex: 0,

    /** Number that represents the height of 1 element of the smartTable in px */
    rowHeight: DEFAULT_SMART_TABLE_ROW_HEIGHT_VALUE,

    /** Grow or shrink list item to fill and fit the list container */
    fitContent: true,

    /** Smart scrollbar  */
    scrollBar: "smart",

    /** Selection context for smartTable */
    selection: {

        /** Enable selection display */
        enableBackgroundDisplay: true,

        /** List selection model used by smartTable */
        selectionType: SELECTION_TYPE.SINGLE
    },

    /** Allows to override the copy function from table to same table, doesn't affect paste. You should re-implement your paste callback */
    overrideCopy: false,

    /** Default: empty columns */
    columns: [],

    /** Height of resizer for sticky rows (in px) */
    stickyRowResizerHeight: 4,

    /** Sticky row min height (in px) */
    stickyRowMinHeight: 26,

    /** Min-width(in px) of columns for their resize, default is 20px */
    minColumnResizeWidth: 20,

    /** Display options */
    ...defaultSmartTableDisplayOptions
};

/** Record of CellTableStyle: use to set a style to cell from column and value of cell */
export type CellTableStyle = Partial<
    Record<keyof CSSStyleDeclaration, () => (string | undefined)> &
    { cssClasses: () => (string[] | undefined) }
>;


/** Type of sticky row, to be displayed either at the top or at the bottom of the table body */
export type SmartTableStickyRowType = "header" | "footer";

export interface SmartTableSticky {

    /** Height of sticky row (set after first resize from user) */
    height?: number;

    /** Disable resizer for row */
    disableResize: boolean; // TODO,

    /** Footer or header */
    stickyType: SmartTableStickyRowType;
}

/** Global index of row of SmartTable */
export type RowGlobalIndex = number;

/**
 * Formatted row of table
 * displayed: Is the row displayed (is the row non filtered)
 * data: Value or function of an itemData
 * index: index of row
 * selected: is the row selected
 */
export interface SmartTableRow<ItemData> {

    /** Is row displayable (may not be displayed on screen) */
    displayed: boolean;

    /** Data of row, do not manipulate directly, you should use smartTable.getRowItemData(row) */
    data: ValueOrFunction<ItemData>;

    /** cache value do not manipulate directly, you should use smartTable.getRowItemData(row) */
    __rowItemDataCache?: ItemData | null;

    /** Uid of the row section (sticky or "")+globalIndex */
    uid: RowUid;

    /** Index of row */
    globalIndex: RowGlobalIndex;

    /** Is row selected */
    selected: boolean;

    /** Allow to show a fixed row */
    sticky?: SmartTableSticky;

    /** HTML element of SmartTableRow, if rendered in DOM */
    tr?: HTMLTableRowElement;

    /** HTML element of SmartTableRow, if rendered in DOM */
    resizerTr?: HTMLTableRowElement;

    /** HTML element to append a row after a parent */
    childTr?: HTMLTableRowElement;

    /** Row height (if rendered in DOM) */
    height?: number;

    /** ms timestamp used to determine if row data is dirty */
    refreshUid?: number;

    /** Hierarchy information */
    hierarchyInfo?: SmartTableHierarchyInfo<ItemData>;

    /** 
     * Store drag handler element of row,
     * defined if row is rendered and drag&drop is enabled
     * Used to add/remove dragHandler during table interactions
     */
    dragHandler?: DragRef;
}

/** Represent a cell of smartTable */
export interface SmartTableCell<ItemData> {

    /** 
     * Value of cell
     * Usually value == ItemData[field], but in case of custom column, can be anything
     */
    value: anyReallyAny;

    /**
     * String value which is displayed in the cell
     * Is undefined if displayed value isn't a string
     */
    stringValue?: string;

    /** Row */
    row: SmartTableRow<ItemData>;

    /** Row item data (value retrieved from smartTable.getRowItemData(cell.row))*/
    rowItemData: ItemData | null;

    /** Column */
    column: SmartTableColumn<ItemData>;

    /** HTML element of SmartTableRow, if rendered in DOM */
    html?: HTMLTableCellElement;
}


//#region Dispatched Event

/** Dispatch when scroll is dispatched on table */
export type SmartTableOnScrollEvent = {

    /** New index of smartTable, index is smartScrollBar firstDisplayedIndex */
    index: number,

    /** the scroll increment that lead to this index. Useless outside smart table scope */
    scrollIncrement?: number
}

export type SmartTableOnResizeStickyEvent = {

    /** New height of the row */
    rowHeight: number,

    /** Index of the sticky row */
    rowIndex: number,

    /** Sticky row is in footer or header ? */
    stickyType: SmartTableStickyRowType
}

/** On table cell clicked */
export type SmartTableOnClickEvent<ItemData> = {

    /**
     * Cell clicked
     */
    cell: SmartTableCell<ItemData>,

    /** Click event */
    clickEvent?: MouseEvent | TouchEvent
};

/** On table cell focused has changed */
export type SmartTableOnFocusChangedEvent<ItemData> = {

    /**
     * Old cell focused
     */
    oldCell: SmartTableCell<ItemData> | undefined,

    /**
     * New cell focused
     */
    newCell: SmartTableCell<ItemData> | undefined,

    /** Native event at origin of focus changed customEvent */
    event?: Event
};

export type SmartTableValueEditOrigin = "paste" | "editor";
export type SmartTableValueEditEvent<ItemData> = {

    /**
     * Edited cell
     */
    cell: SmartTableCell<ItemData>,

    /** 
     * New value set
     *
     * Usually value == ItemData[field], but in case of custom column, can be anything
     */
    newValue: anyReallyAny,

    /**
     * Action that should be used to update the cell object value
     */
    action?: Action,

    /**
     * Origin of the edit
     */
    origin: SmartTableValueEditOrigin
}

export type SmartTableEditionFinishEvent<ItemData> = {

    /**
     * Cell edited
     */
    cell: SmartTableCell<ItemData>,

    /** New value set */
    newValue: anyReallyAny
}

/** On collapse or expand row */
export type SmartTableRowCollapseEvent<ItemData> = {

    /**
     * Row collapse or expanded
     */
    row: SmartTableRow<ItemData>,

    /** Row collapsed */
    collapsed: boolean
}

/** On loop detection in hierarchy */
export type SmartTableLoopDetectedEvent<ItemData> = {

    /**
     * Row where loop has been detected
     */
    row: SmartTableRow<ItemData>
}

/** 
 * On resize column event 
 */
export interface SmartTableColumnResizeEvent<ItemData> {

    /** Column which we triggered the resize event on */
    column: SmartTableColumn<ItemData>;

    /** Column width at instant t */
    columnWidth: number;

    /** Horizontal offset from the start resize position */
    offsetX: number;
}

/** 
 * On set data event 
 */
export interface SmartTableSetDataEvent<ItemData> {
    /** Set data */
    data: ItemData[];
}

export type SmartTableOnSortEvent<ItemData> = {
    /** Columns sorted */
    columnsSorted: ColumnSorted<ItemData>[]
}

//#endregion


/** Object returned by calcDisplayedItemCount function  */
export interface CalcDisplayedItemCount {

    /** Count of items displayed */
    displayedItemCount: number;

    /** Store total height taking by rows */
    rowsHeightCpt: number;

    /** Count of emptyRows to display */
    emptyRowsCount: number;
}

/** Value of clearConfiguration params */
export interface SmartTableClearConfigurationParams {

    /** Clear columns sort status */
    sort?: boolean;

    /** Clear columns filter status */
    filters?: boolean;

    /** Clear global hierarchy state */
    hierarchy?: boolean;

    /** Clear global selection state */
    selection?: boolean;
}

/**
 * Return true if both rows uid are equal
 * @param row1 First row to compare
 * @param row2 Second row to compare
 */
export const compareSmartTableRowsByUid = <ItemData>(row1?: SmartTableRow<ItemData>, row2?: SmartTableRow<ItemData>): boolean => {
    return row1?.uid === row2?.uid;
};

@CustomElement({
    name: "cjs-smart-table",
    style: require("./smartTable.scss"),
    template: require("./smartTable.html"),
    // H_100 : the smart table must have a height defined, otherwise if its parent does not impose a height it may resize after each render (since we always display the last row even if it is truncated on display)
    elementCssClasses: [FLEX_CONTAINER, FLEX_ITEM_AUTO, H_100],
    i18n
})
export class CjsSmartTable<ItemData> extends WebComponent<I18nType> {

    @TypedWebComponentOptions(defaultSmartTableOptions)
    public readonly options!: WebComponentOptions<SmartTableOptions<ItemData>>;

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @Injected(EDITOR_FACTORY_SERVICE)
    protected readonly _editorFactory!: EditorFactoryService;

    @TypedHtmlElements()
    public readonly components!: {
        smartTableContainer: HTMLDivElement,
        table: HTMLTableElement,
        tableHead: HTMLElement,
        tableBody: HTMLElement,
        tableFooter: HTMLElement,
        scrollbarContainer: HTMLDivElement,
        headerSuffix: HTMLDivElement,
        stickyHeaderSuffix: HTMLDivElement,
        footerSuffix: HTMLDivElement
    };

    /**
     * Event dispatched when a row is clicked
     */
    @Event(NoBubblesEventInit)
    public rowClickEvent!: EventHandler<SmartTableOnClickEvent<ItemData>>;

    /**
     * Event dispatched when selection has change by row click event or by checkbox selection
     * * Use a shortcut to event in selection model
     */
    @Event(NoBubblesEventInit)
    public get selectionChangeEvent(): EventHandler<SelectionChange<ItemData>> {
        return this._selectionModel.selectionChangedEvent;
    }

    /**
     * Event dispatched when the cell focused has changed
     */
    @Event(NoBubblesEventInit)
    public focusChangeEvent!: EventHandler<SmartTableOnFocusChangedEvent<ItemData>>;

    /**
     * Event dispatched when table has scrolled
     */
    @Event(NoBubblesEventInit)
    public scrollEvent!: EventHandler<SmartTableOnScrollEvent>;

    /**
     * Event dispatched when a filter is applied to table rows
     */
    @Event(NoBubblesEventInit)
    public filterEvent!: EventHandler<SmartTableOnFilterEvent<ItemData>>;

    /**
     * Event dispatched when a search is applied to table rows
     */
    @Event(NoBubblesEventInit)
    public searchEvent!: EventHandler<void>;

    /**
     * Event dispatched when a custom filter is applied to table rows
     */
    @Event(NoBubblesEventInit)
    public advancedFilterEvent!: EventHandler<void>;

    /**
     * Event dispatched when a filter is applied with excel like component
     */
    @Event(NoBubblesEventInit)
    public excelLikeFilterEvent!: EventHandler<void>;

    /**
     * Event dispatched when user hits enter key in filter
     */
    @Event(NoBubblesEventInit)
    public filterEnterKeyEvent!: EventHandler<SmartTableOnFilterEvent<ItemData>>;

    /**
     * Event dispatched when a sort is applied to table rows
     */
    @Event(NoBubblesEventInit)
    public sortEvent!: EventHandler<SmartTableOnSortEvent<ItemData>>;

    /**
     * Event dispatched when a sticky row is resized
     */
    @Event(NoBubblesEventInit)
    public onResizeStickyContainer!: EventHandler<SmartTableOnResizeStickyEvent, EventInit>;

    /**
     * Event dispatched when a value is edited in a cell
     */
    @Event(NoBubblesEventInit)
    public cellEditedValueEvent!: EventHandler<SmartTableValueEditEvent<ItemData>>;

    /**
     * Event dispatched when a value has been edited in a cell
     * (will dispatch cellEditedValueEvent if value changed)
     */
    @Event(NoBubblesEventInit)
    public cellEditionFinishEvent!: EventHandler<SmartTableEditionFinishEvent<ItemData>>;

    /**
     * Event dispatched when a row is collapse or expand
     */
    @Event(NoBubblesEventInit)
    public rowCollapseEvent!: EventHandler<SmartTableRowCollapseEvent<ItemData>>;

    /**
     * Event dispatched when all rows are expanded or collapsed
     */
    @Event(NoBubblesEventInit)
    public allCollapseOrExpandEvent!: EventHandler<void>;

    /**
     * Event dispatched when a loop is detected in the hierarchy model
     */
    @Event(NoBubblesEventInit)
    public hierarchyLoopEvent!: EventHandler<SmartTableLoopDetectedEvent<ItemData>>;

    /**
     * Event dispatched when mouse down on the resizer
     */
    @Event(NoBubblesEventInit)
    public startColResizeEvent!: EventHandler<SmartTableColumnResizeEvent<ItemData>>;

    /**
     * Event dispatched when resizing a column
     */
    @Event(NoBubblesEventInit)
    public resizingColEvent!: EventHandler<SmartTableColumnResizeEvent<ItemData>>;

    /**
     * Event dispatched when mouse up after resizing
     */
    @Event(NoBubblesEventInit)
    public endColResizeEvent!: EventHandler<SmartTableColumnResizeEvent<ItemData>>;

    /**
     * Event dispatched when data are set
     */
    @Event(NoBubblesEventInit)
    public setDataEvent!: EventHandler<SmartTableSetDataEvent<ItemData>>;

    /**
     * Event dispatched when render is finished
     */
    @Event(NoBubblesEventInit)
    public renderFinishEvent!: EventHandler<{ reason: string }>;

    /** Get table element of smartTable */
    public get table(): HTMLTableElement {
        return this.components.table;
    }

    /** 
     * Rows of table (set in setData)
     * Does not contain sticky rows
     */
    private _tableRows: SmartTableRow<ItemData>[] = [];

    /**
     * Rows of table (set in setData)
     * Does not contain sticky rows
     * ! public access is provided for convenience but you should not manipulate this array directly
     */
    public get tableRows(): SmartTableRow<ItemData>[] {
        return this._tableRows;
    }

    /**
     * Rows currently displayable in table (ie filtered) - set in refresh function
     * Does not contain sticky rows
     */
    private _displayableRows: SmartTableRow<ItemData>[] = [];

    /**
     * Rows currently displayable in table (ie filtered) - set in refresh function
     * Does not contain sticky rows
     * ! public access is provided for convenience but you should not manipulate this array directly
     */
    public get displayableRows(): SmartTableRow<ItemData>[] {
        return this._displayableRows;
    }

    /** 
     * Rows currently displayed by smartTable 
     */
    private _displayedRows: SmartTableRow<ItemData>[] = [];

    /**
     * Get rows currently displayed by smartTable 
     * ! public access is provided for convenience but you should not manipulate this array directly
     */
    public get displayedRows(): SmartTableRow<ItemData>[] {
        return this._displayedRows;
    }

    /** 
     * Last Table Row that belongs to a sticky row (can be a resizer)
     */
    private _lastHeaderStickyTr?: HTMLTableRowElement;

    /** ms timestamp used to determine if row data is dirty */
    private _refreshUid = 0;

    /** ms timestamp used to determine if row data is dirty */
    public get refreshUid(): number {
        return this._refreshUid;
    }

    /** Store bounds of smartTable after last render */
    public lastRenderBounds?: SmartTableBounds;

    /**
     * _rendering is true during render 
     */
    private _rendering = false;

    /**
     * if render is called before current render is finished, _renderingIsDirty is set to true, and a new render will be done after current render
     */
    private _renderingIsDirty = false;

    /** 
     * Sum of all sticky header rows height
     */
    private _totalStickyHeaderRowsHeight = 0;

    /** 
     * Sum of all sticky footer rows height
     */
    private _totalStickyFooterRowsHeight = 0;

    /** Visible height of table body */
    protected _bodyVisibleHeight = -1;

    /** 
     * Css row border height - set in displayHelper.refreshDisplayStyle (=> public)
     * this property does not affect the border, it is used to compute the height taken by each row
     */
    public rowCssBordersHeight = 0;

    /** 
     * Available space still available below rows
     */
    private _availableHeight = 0;

    /** Empty row that can be added to the table body to ensure we keep row heights valid */
    private _additionalEmptyRow?: HTMLElement;
    private _isAdditionalEmptyRowInDom = false;

    /** Scrollbar of smartTable (linked to this) */
    public scrollBar = new CjsSmartScrollBar({ elementToHandle: this.components.tableBody, doNotUseMobilePanEvents: () => !this.isSmart() });

    /** Style rendered of smartTable */
    public styleHelper: SmartTableStyleHelper<ItemData> = new SmartTableStyleHelper(this);

    /** Cells by rowUid, columnUid */
    protected _cellsByRowAndColumn: Record<RowUid, Record<ColumnUid, SmartTableCell<ItemData>>> = {};
    /** Getter for cellsByRowAndColumn */
    public get cellsByRowAndColumn(): Record<RowUid, Record<ColumnUid, SmartTableCell<ItemData>>> {
        return this._cellsByRowAndColumn;
    }

    /** Store cellsByRow uid cache */
    protected _cellsByRowCache?: Record<RowUid, SmartTableCell<ItemData>[]>;
    /** Getter for cellsByRow */
    public get cellsByRow(): Record<RowUid, SmartTableCell<ItemData>[]> {
        if (this._cellsByRowCache == null) {
            this._cellsByRowCache = {};
            // Loop into all cells to fill cellsByRow
            for (const [rowUid, colCell] of Object.entries(this._cellsByRowAndColumn)) {
                for (const cell of Object.values(colCell)) {
                    addToArrayRecord(this._cellsByRowCache, rowUid, cell);
                }
            }
        }
        return this._cellsByRowCache;
    }

    protected _cellsByColumnCache?: Record<RowUid, SmartTableCell<ItemData>[]>;
    /** Getter for cellsByColumn */
    public get cellsByColumn(): Record<RowUid, SmartTableCell<ItemData>[]> {
        if (this._cellsByColumnCache == null) {
            this._cellsByColumnCache = {};
            // Loop into all cells to fill cellsByColumn
            for (const colCell of Object.values(this._cellsByRowAndColumn)) {
                for (const [colUid, cell] of Object.entries(colCell)) {
                    addToArrayRecord(this._cellsByColumnCache, colUid, cell);
                }
            }
        }
        return this._cellsByColumnCache;
    }

    /** Edition cell content helper of smartTable */
    public editionHelper: SmartTableEditionHelper<ItemData> = new SmartTableEditionHelper(this);

    /** Table rows selection Model */
    protected _selectionModel = new SmartTableSelectionModel<ItemData>(this);

    /** Get rowHeight */
    public getRowHeight(row?: SmartTableRow<ItemData>): number {

        let height = 0;
        if (typeof this.options.rowHeight === "number") {
            height = this.options.rowHeight;
        } else if (row) {
            // If data is a Function, return is call
            const itemData = this.getRowItemData(row);
            height = this.options.rowHeight(itemData) ?? DEFAULT_SMART_TABLE_ROW_HEIGHT_VALUE;
        }

        if (row?.sticky) {
            height = row.sticky.height ?? height;
            // Handle min height of stickyRow
            height = this._adjustStickyRowHeightIfNeeded(height, row);
        }

        return height;
    }

    /** adjust to minHeight */
    protected _adjustStickyRowHeightIfNeeded(height: number, row: SmartTableRow<ItemData>): number {
        const minHeight = typeof this.options.stickyRowMinHeight === "function" ? this.options.stickyRowMinHeight(row) : this.options.stickyRowMinHeight;
        if (this.isStickyRowResizeDisabled(row)) {
            // not resizable => set min height
            height = row.height ?? minHeight;
        } else {
            // Handle min height of stickyRow
            if (minHeight != null && height < minHeight) {
                height = minHeight;
            }
        }
        return height;
    }

    /**
     * @returns the height that the row will have once rendered
     */
    public computeRowHeight(item: SmartTableRow<ItemData>): number {
        return this.getRowHeight(item);
    }

    /** Rows of table, never updated, used for sort */
    protected _initialTableRows: SmartTableRow<ItemData>[] = [];
    /** Access initialTableRows */
    public get initialTableRows(): SmartTableRow<ItemData>[] {
        return this._initialTableRows;
    }

    /** Sticky rows, will not be removed on scroll */
    protected _stickyTableRows: SmartTableRow<ItemData>[] = [];

    /** Access _stickyTableRows */
    public get stickyTableRows(): SmartTableRow<ItemData>[] {
        return this._stickyTableRows;
    }

    /** Helper for filters of smartTable */
    public filterHelper = new SmartTableFilterHelper<ItemData>(this);

    /** Helper for search of smartTable */
    public searchHelper = new SmartTableSearchHelper<ItemData>(this);

    /** Helper for hierarchy in smartTable */
    public hierarchyHelper = new SmartTableHierarchyHelper<ItemData>(this);

    /** StickyRows can be resized */
    protected _canResizeStickyRow = true;

    /**
     * Store rows by uid cache
     * Refreshed into setData()
     * @see setData
     */
    public rowsByUid = new Map<RowUid, SmartTableRow<ItemData>>();

    /**
     * Store cols by uid cache
     * Refreshed into setData()
     * @see setData
     */
    public colsByUid = new Map<ColumnUid, SmartTableColumn<ItemData>>();

    /** Helper for sort in the smartTable */
    public sortHelper = new SmartTableSortHelper<ItemData>(this);

    /** Helper for drag and drop in the smartTable */
    public dragAndDropHelper = new SmartTableDragAndDropHelper<ItemData>(this);

    /** Contains resizer div html element for each columns */
    private _resizerColumnDivList: HTMLElement[] = [];

    /** elements to dispose when calling dispose function */
    protected _elementsToDispose: {
        hammer?: HammerManager,
        resizeObserver?: ResizeObserver
    } = {};

    protected _isDisposed = false;

    constructor(options?: Partial<SmartTableOptions<ItemData>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        setDisposable(this);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Cleanup
     */
    public dispose(p?: { doNotDisposeChildren?: boolean }): void {
        if (this._isDisposed) {
            return;
        }
        this._isDisposed = true;
        disposeHammer(this._elementsToDispose);
        disposeResizeObserver(this._elementsToDispose);

        // Empty all lists
        if (this._initialTableRows) {
            for (const r of this._initialTableRows) {
                destroyObject(r);
            }
        }
        this._initialTableRows.length = 0;
        this._displayedRows.length = 0;
        this._tableRows.length = 0;
        this._displayableRows.length = 0;
        this._resizerColumnDivList.length = 0;
        this.rowsByUid.clear();
        this.colsByUid.clear();

        this.disconnectedCallback();

        destroyObject(this.hierarchyHelper);
        destroyObject(this.sortHelper);
        destroyObject(this.styleHelper);
        destroyObject(this.filterHelper);
        destroyObject(this.searchHelper);
        destroyObject(this.editionHelper);

        // Clear map of cells
        this._clearCells();

        this._additionalEmptyRow = undefined;
        if (!p?.doNotDisposeChildren) { disposeChildren(this); }
        this.lightDomRoot = undefined;
    }

    //#region Page lifeCycle

    /**
     * Init component
     */
    public override init(): void {
        if (this._isDisposed) {
            // element was disposed => don't build it
            return;
        }

        // set timeline related options
        if (this.options.withinTimeline) {
            this.options.setWithoutRefresh({
                doNotObserveResize: true, // resize is observed by timeline
                autoScrollUpDisabled: true, // first display item is handled by timeline row view
                rowHeightDrivenExternally: true // update row height on each render
            });
        }

        // Append scrollbar to his container
        this.components.scrollbarContainer.append(this.scrollBar);
        this.scrollBar.installKeyListeners(this);

        // Move footer suffixes after scrollbar
        this.components.scrollbarContainer.append(this.components.footerSuffix);

        // Init table listeners
        this._initListeners();
    }

    /**
     * init  excelLikeSortFilter or columnConfigurator
     */
    public prepareHeaderRender(): void {
        // Refresh column configs by adding missing columns on column configs if need be
        if (this.options.excelLikeSortFilter?.enable || this.options.columnConfigurator?.enable) {
            getColumnsFromConfiguration(this.options.columns, this.options.columnsConfig, true);
        }
        // Refresh excel like sort filter instantiation
        if (this.options.excelLikeSortFilter?.enable) {
            this.filterHelper.refreshExcelLikeSortFilter(this.options.excelLikeSortFilter.options);
        }

        // Refresh column configurator instantiation 
        // init columnConfigurator
        if (this.options.columnConfigurator?.enable) {
            this.filterHelper.refreshColumnConfigurator();
        }
    }

    /** refresh data from options and render */
    public override refresh(reason?: string, renderOptions?: { headerIsAlreadyRendered?: boolean, renderCacheData?: RenderCacheData }): void {
        if (this.options.withinTimeline && reason === "init") {
            // refresh is driven by timeline and init refresh will be wrong and useless
            return;
        }
        if (this._isDisposed) {
            this._log?.error("Element is disposed");
            return;
        }


        // if smartTable isn't smart, enable option to setup smartTable into classic table
        if (this.options.smart === false) {
            this.options.setWithoutRefresh({
                parentContainerHasNoHeightConstraint: true,
                scrollBar: "standard"
            });
        }

        const debugTrace = this.debug.traceStart("refresh", reason);

        renderOptions ??= {};
        renderOptions.renderCacheData ??= {};

        try {

            if (!renderOptions.headerIsAlreadyRendered) {
                // Refresh excel like sort filter instantiation
                // Refresh column configurator instantiation 
                this.prepareHeaderRender();
            }

            this.styleHelper.refreshDisplayStyle();
            this._refreshScrollShadowsVisibility();
            this.refreshHorizontalScrollbar();

            if (reason !== "sort" && reason !== "resize" && reason !== "scroll") {
                // reset cache
                this._refreshUid = Date.now();
            }
            // reset background colors for cell
            this.hierarchyHelper.resetCellBackground();

            // Render table
            this.render(reason ?? "refresh", renderOptions);

            this.debug.traceFinish(debugTrace);
        } catch (err) {
            this.debug.traceError(debugTrace, err);
            this._log?.error(err);
        }
    }

    /**
     * Set data of tableComponent
     * @param p
     * @param {ItemData[]} p.data to set in table
     * @param {ItemData[]} p.stickyData to set in table, must contain sticky header and footer data
     * @param {boolean} p.refresh true => ask for a table refresh or not
     * @param p.clear state of smartTable to clear sort or filter after setData()
     */
    public setData(p: {
        data: ItemData[],
        stickyData?: { stickyHeaderData: ItemData[], stickyFooterData: ItemData[] },
        refresh?: boolean,
        clear?: SmartTableClearConfigurationParams
    }): void {
        if (this._isDisposed) {
            this._log?.error("Element is disposed");
            return;
        }

        // ! Always clear cache map of cells after a setData (cache is invalid with new data)
        this._clearCells();

        // Set refresh default value
        if (p.refresh !== false) { p.refresh = true; }

        p.stickyData = p.stickyData ?? { stickyHeaderData: [], stickyFooterData: [] };

        const nbStickyHeader = p.stickyData.stickyHeaderData.length;

        const getStickyRow = (data: ItemData, i: number, stickyType: SmartTableStickyRowType, previousSticky?: SmartTableSticky): SmartTableRow<ItemData> => {
            return {
                displayed: true,
                data,
                uid: this.options.getRowUid?.(data, i) ?? "sticky" + ((stickyType === "footer" ? nbStickyHeader : 0) + i),
                globalIndex: (stickyType === "footer" ? nbStickyHeader : 0) + i,
                sticky: {
                    disableResize: previousSticky?.disableResize ?? false,
                    height: previousSticky?.height ?? undefined,
                    stickyType
                },
                selected: false
            };
        };
        // Init sticky rows
        this._stickyTableRows = [
            ...p.stickyData.stickyHeaderData.map((d, i) => {
                /** Keep sticky properties on re-setData */
                const previousSticky = this._stickyTableRows[i]?.sticky;
                return getStickyRow(d, i, "header", previousSticky);
            }),
            ...p.stickyData.stickyFooterData.map((d, i) => {
                /** Keep sticky properties on re-setData */
                const previousSticky = i === 0 ? undefined : this._stickyTableRows[i]?.sticky;
                return getStickyRow(d, i, "footer", previousSticky);
            })];

        // Toggle suffix visibility
        toggleVisible(this.components.stickyHeaderSuffix, p.stickyData.stickyHeaderData.length > 0, Display.FLEX);

        // Clear configuration if needed
        this.clearConfiguration({ ...p.clear, refresh: false });

        // Store a copy of rows into initialTableRows, never updated
        let rows: SmartTableRow<ItemData>[];
        if (!this.options.hierarchyOptions) {
            rows = p.data.map((d, i) => {
                return is<SmartTableRow<ItemData>>({
                    displayed: true,
                    data: d,
                    uid: this.options.getRowUid?.(d, i) ?? String(i),
                    globalIndex: i,
                    selected: false
                });
            });
        } else {
            // if getParentFunction has been defined, rows are created with updated hierarchy information and sorted accordingly
            // needs to be done before cleanup references
            rows = this.hierarchyHelper.createRowsWithHierarchy(p.data, { clear: p.clear?.hierarchy });
        }
        if (this._initialTableRows) {
            // Cleanup references (to ease garbage collecting)
            for (const r of this._initialTableRows) {
                destroyObject(r);
            }
        }

        // Sort rows by globalIndex
        sortFields(rows, "globalIndex");

        this._initialTableRows = rows;

        // init table rows
        this._tableRows = [...this._initialTableRows];
        this.rowsByUid = buildObjectMap(this.tableRows, "uid");

        // set rows to display (no filter by default)
        this._displayableRows = [...this._tableRows];

        const renderCacheData: RenderCacheData = {};

        // Always apply filter to apply advanced filters
        this.filterHelper.filterTable({ renderCacheData, refresh: false, doNotClearHierarchy: p.clear?.hierarchy === false });

        if (!p.clear?.sort) {// Useless to call sort if sort was cleared
            // table rows and displayable rows are set after being sorted 
            this.sortHelper.sortTable({ renderCacheData, noRefresh: true, noEmit: true });
        }

        // Refresh is required
        if (p.refresh && this.isInitDone) {

            if (p.clear?.selection !== false) {
                // Clear selection model
                this.getSelectionModel().clear({ doNotDispatchEvent: true, doNotRefreshSmartTable: true });
            } else {
                this.getSelectionModel().refreshRowsSelectionInSmartTable();
            }

            // if !isInitDone refresh will be called later
            this.refresh("setData", { renderCacheData });
        }

        // Emit set data event
        this.setDataEvent.emit({ data: p.data });
    }

    //#region Rendering

    /**
     * Render table
     */
    public render(reason: string, options?: { scrollIncrement?: number, headerIsAlreadyRendered?: boolean, renderCacheData?: RenderCacheData }): void {
        if (this._rendering) {
            // force rerender
            this._renderingIsDirty = true;
            return;
        }
        this._renderingIsDirty = false;
        this._rendering = true;

        try {
            this._renderImpl(reason, options);
        } catch (err) {
            (this._log ?? console).error(err);
        } finally {
            this._rendering = false;
        }

        if (this._renderingIsDirty) {
            // render was called during this render processing, force re-render 
            this.render("dirty", options);
        }
    }

    /**
     * Enable/Disable horizontal scroll on table container
     */
    public refreshHorizontalScrollbar(): void {
        toggleClass(this.components.smartTableContainer, this.options.enableHorizontalScroll, HORIZONTAL_OVERFLOW_CLASS);
    }

    /** 
     * @returns horizontalScrollbarHeight if horizontal scrollbar is visible, 0 otherwise
     */
    public getHorizontalScrollbarHeight(): number {
        const scrollingContainer = this.components.smartTableContainer;
        const isHorizontalScrollBarVisible = scrollingContainer.scrollWidth > scrollingContainer.clientWidth;
        if (isHorizontalScrollBarVisible) {
            const horizontalScrollbarHeight = Math.ceil(scrollingContainer.offsetHeight - scrollingContainer.clientHeight);
            return horizontalScrollbarHeight;
        }
        return 0;
    }

    /**
     * @returns the body height (subtracts header, footer and horizontalScrollbar / does not consider sticky rows)
     */
    public computeBodyHeight(smartTableBounds: SmartTableBounds): number {
        const horizontalScrollbarHeight = this.getHorizontalScrollbarHeight();
        const headerHeight = Math.ceil(smartTableBounds.header.height);
        const footerHeight = Math.ceil(smartTableBounds.footer.height);
        const totalHeight = Math.floor(smartTableBounds.smartTable.height);
        // Determine available height
        const bodyVisibleHeight = totalHeight - headerHeight - footerHeight - horizontalScrollbarHeight;
        return bodyVisibleHeight;
    }

    /**
     * Render table implementation function (should not be called directly, you should call render)
     */
    // TODO BGS handle complexity
    // eslint-disable-next-line complexity
    private _renderImpl(reason: string, options?: { scrollIncrement?: number, headerIsAlreadyRendered?: boolean, renderCacheData?: RenderCacheData }): void {
        // debug code => keep commented
        // console.log("smart table render", reason, "rows", this._displayableRows.length, "first", this.options.firstDisplayedItemIndex, "height", this.clientHeight, "width", this.clientWidth, "scrollIncrement", options?.scrollIncrement);

        // Toggle not smart class
        toggleClass(this, !this.isSmart(), TABLE_NOT_SMART_CLASS);

        // if scrollbar is standard, keep scroll position
        const initialScrollTop = this.options.scrollBar === "standard" ? this.components.smartTableContainer.scrollTop : undefined;

        // ensure firstDisplayedItemIndex is valid
        if (this.options.firstDisplayedItemIndex < 0 || !this.isSmart()) {// If not smart, firstDisplayedItemIndex is always "0"
            this.options.setWithoutRefresh({
                firstDisplayedItemIndex: 0
            });
        }

        // Display error if list of columns has uids duplicates
        checkColumnDuplicatesUid(this.options.columns, { displayLogError: true });

        // Store columns by uid, build map at each render, can be updated between two refresh
        this.colsByUid = buildObjectMap(this.options.columns, "uid");

        // Sort columns by 'order' field 
        this.sortColumns();

        // Record passed to all getCellValue calls during a single render other table that allows application to store tmp data
        const renderCacheData: RenderCacheData = options?.renderCacheData ?? {};

        // prepareForRender
        for (const col of getVisibleColumnsFromConfiguration(this.options.columns, this.options.columnsConfig)) {
            col.prepareForRender?.(renderCacheData);
        }

        if (this.lastRenderBounds?.uid !== this._refreshUid) {

            // a change on header, footer or sticky rows can only be triggered by a call to refresh (=> a change of _refreshUid)
            if (!options?.headerIsAlreadyRendered) {
                this.renderHeader(renderCacheData);
            }
            this.renderFooter(renderCacheData);

            // Clear table
            empty(this.components.tableBody);
            this._isAdditionalEmptyRowInDom = false;

            // Empty all list
            this._displayedRows = [];

            // Clear map of cells
            this._clearCells();

            // init stick rows
            this._totalStickyHeaderRowsHeight = 0;
            this._totalStickyFooterRowsHeight = 0;
            this._lastHeaderStickyTr = undefined;
            for (const stickyRow of this._stickyTableRows) {
                this._populateRow(stickyRow, { renderCacheData });
                this._appendStickyRow(stickyRow);
                if (stickyRow.sticky?.stickyType === "header") {
                    this._lastHeaderStickyTr = stickyRow.resizerTr ?? stickyRow.tr;
                }
            }

            // append an empty row is needed with firefox to correctly compute tableHead and tableFooter offsetHeights
            this._appendEmptyRow();
        }

        const stickyHeights = this._computeTotalStickyRowsHeight();
        this._totalStickyHeaderRowsHeight = stickyHeights.headerHeight;
        this._totalStickyFooterRowsHeight = stickyHeights.footerHeight;

        // computeTableBounds and assign new refreshUid
        const smartTableBounds = this.computeTableBounds();

        // Determine available height
        this._bodyVisibleHeight = this.computeBodyHeight(smartTableBounds) - this._totalStickyHeaderRowsHeight;// we don't have "- stickyHeights.footerHeight" because it is not within table body
        this._availableHeight = this._bodyVisibleHeight;

        // Append all elements
        let displayedRowIndex = 0;
        let previousRowTr: HTMLTableRowElement | undefined = this._lastHeaderStickyTr;

        for (let i = this.options.firstDisplayedItemIndex; i < this._displayableRows.length; i++) {
            const row = this._displayableRows[i];

            let displayedRow: SmartTableRow<ItemData> | undefined = this.displayedRows[displayedRowIndex];
            if (options?.scrollIncrement && options.scrollIncrement > 0 && options.scrollIncrement > displayedRowIndex) {
                // scrolling up => ignore displayed row until scrollIncrement reached
                displayedRow = undefined;
            }

            while (displayedRow != null && displayedRow !== row) {
                this._removeDisplayedRow(displayedRowIndex);
                displayedRow = this.displayedRows[displayedRowIndex];
            }

            if (displayedRow === row && row.tr) {
                // already in DOM at the right position => nothing to do, except refresh dragHandler (displayed only if activated and if there is no sort or filter)
                this.dragAndDropHelper.refreshCellDragHandler(row);
            } else {
                this._populateRow(row, { renderCacheData });
                this._appendRow(row, { insertAfter: previousRowTr, insertIndex: displayedRowIndex });
            }

            this._adjustRowHeightIfNeeded(row);
            previousRowTr = this.hierarchyHelper.canInsertRowChild(row) ? row.childTr : row.tr;
            displayedRowIndex++;

            // Check if the row has an expanded child tr to include its height in the available height calculation
            this._subtractAvailableHeight(row);
            if (this._availableHeight <= 0) {

                if (this.options.parentContainerHasNoHeightConstraint && this._availableHeight > -1000) {
                    // we continue to render 1000 pixels by 1000 pixels until we reach a limit in a above container...
                } else if (this.options.scrollBar === "standard") {
                    // we render all rows
                } else {
                    break;
                }
            }
        }

        // remove rows that should not be there anymore
        while (this.displayedRows.length > displayedRowIndex) {
            this._removeDisplayedRow(displayedRowIndex);
        }

        if (!this.options.autoScrollUpDisabled) {
            // if the firstDisplayedItemIndex is too high, it will be adjusted in order to display a maximum of rows

            if (this.options.firstDisplayedItemIndex > this._displayableRows.length) {
                // move firstDisplayedItemIndex directly to this._displayableRows.length to ensure we have a valid row on the first iteration of the below while loop
                this.options.setWithoutRefresh({ firstDisplayedItemIndex: this._displayableRows.length });
            }
            while (this.options.firstDisplayedItemIndex > 0 && this._availableHeight > 0) {
                if (this.options.allowScrollUntilLastRow && this.options.firstDisplayedItemIndex < this._displayableRows.length) {
                    // we only scroll if no rows are displayed
                    break;
                }
                // component size or data have changed and we need to adjust first index and scroll up because there is too much space below last displayed row
                const i = this.options.firstDisplayedItemIndex - 1;
                const row = this._displayableRows[i];
                if (this.hierarchyHelper.canInsertRowChild(row)) {
                    // Don't continue if row child has been appended
                    break;
                }
                const rowHeight = this.getRowHeight(row);
                if (this._availableHeight < rowHeight + this.rowCssBordersHeight) {
                    // not enough room to display full row => don't scroll up
                    break;
                }
                this.options.setWithoutRefresh({ firstDisplayedItemIndex: i });
                // insert row above others
                this._populateRow(row, { renderCacheData });
                this._appendRow(row, { insertAfter: this._lastHeaderStickyTr, insertIndex: 0 });
                this._subtractAvailableHeight(row);
            }
        }

        const hasStickyFooterRow = this.stickyTableRows.some(r => r.sticky?.stickyType === "footer");
        if (this._availableHeight > 0 && this.options.firstDisplayedItemIndex === 0 && !this.options.enableFooter && !hasStickyFooterRow) {
            // not scrolled and not full and no footer => fit-content to prevent any height computation by the table
            this.components.table.style.height = "fit-content";
            this._removeEmptyRow();
        } else {
            if (this._availableHeight > 0) {
                // we add an empty row to ensure rows don't expand above their 'natural' height
                this._appendEmptyRow();
            }// we don't remove empty row on else because it breaks the display on firefox (ex: cellStyle show)
            // we take all the available space, any additional row will be clipped since we have overflow hidden
            this.components.table.style.height = "100%";
        }

        // refresh selection display
        this.styleHelper.refreshRowsSelectionDisplay();
        this.styleHelper.installCrossHover();

        // keep consistent stripes on scroll
        toggleClass(this, this.options.firstDisplayedItemIndex % 2 === 1, INVERT_STRIPES_CLASS);

        // finalizeRender
        for (const col of getVisibleColumnsFromConfiguration(this.options.columns, this.options.columnsConfig)) {
            if (col.finalizeRender) { col.finalizeRender(renderCacheData); }
        }

        // Refresh scrollBar, important to refresh scrollbar in render to update thumb display on resize for example
        this._refreshScrollbar();

        // Set height of resizable div to be able to change the width wherever in the table and not only in the header
        const smartTableHeight = this.components.tableHead.offsetHeight;
        this._resizerColumnDivList.forEach(div => div.style.height = toPx(smartTableHeight));

        if (this.options.scrollBar === "standard") {
            if (initialScrollTop != null) {
                // if measure at the start of the function is done in fastDom.measure, it does not work
                // if modification is done in fastDom.mutate, the screen blinks
                this.components.smartTableContainer.scrollTop = initialScrollTop;
                this.refreshFirstLineStickyContent();
            }
        }

        // If search is enable, refresh researched cells style after each render
        if (this.options.search?.enable) {
            this.searchHelper.refreshSearchCellsStyle();
        }

        // Dispatch render finish event
        this.renderFinishEvent.emit({ reason });
    }

    /**
     * If the table row heights are driven by another component, request the row height and update tr
     */
    protected _adjustRowHeightIfNeeded(row: SmartTableRow<ItemData>): void {
        if (this.options.rowHeightDrivenExternally && row.tr) {
            const rowHeight = this.getRowHeight(row);
            row.height = rowHeight;
            row.tr.style.height = rowHeight + "px";
        }
    }

    /** if firstLineStickyContent, adjusts the cell padding top in order to always display the top of the first line */
    public refreshFirstLineStickyContent(resetScrollTopIfInvalid: boolean = true): void {
        if (this.options.firstLineStickyContent) {
            // adjusts the cell padding top in order to always display the top of the first line
            const scrollTop = this.components.smartTableContainer.scrollTop;
            let y = 0;
            for (const row of this.displayedRows) {
                if (row.height && row.tr) {
                    row.tr.style.removeProperty("--scroll-padding");
                    if (y < scrollTop && y + row.height > scrollTop) {
                        // row is cut
                        const padding = scrollTop - y;
                        row.tr.style.setProperty("--scroll-padding", toPx(padding));
                    }
                    y += row.height;
                }
            }

            if (resetScrollTopIfInvalid && y < scrollTop) {
                // scrollTop is invalid => reset
                this.components.smartTableContainer.scrollTop = 0;
                this.refreshFirstLineStickyContent(false);
            }
        }
    }

    /**
     * @returns the column title element or string
     */
    public getColumnTitle(col: SmartTableColumn<ItemData>, colHeaderElement?: HTMLTableCellElement): AcceptedHtmlElementContent {
        const pcLabel = col.propertyConfig?.label ?? "";
        if (typeof col.title === "function") {
            if (colHeaderElement) {
                return col.title(colHeaderElement) ?? pcLabel;
            }
            return pcLabel;
        }
        return col.title ?? pcLabel;
    }

    /**
     * @returns the column footer element or string
     */
    protected _getColumnFooter(col: SmartTableColumn<ItemData>, colFooterElement: HTMLTableCellElement): AcceptedHtmlElementContent {
        if (typeof col.footer === "function") {
            return col.footer(colFooterElement) ?? col.propertyConfig?.label ?? "";
        }
        return col.footer ?? col.propertyConfig?.label ?? "";
    }

    /**
     * Default column TH render with sort and filter
     */
    public defaultRenderColumnHeader(col: SmartTableColumn<ItemData>, params?: { displayToggleIconOnHeader?: boolean }): HTMLElement {
        const colHeaderElement = createHtmlElement("th", { cssClass: [PADDING.P_0] });
        if (col.sticky) {
            addStickyColumn(this, col, colHeaderElement);
        }
        colHeaderElement.setAttribute("scope", "col");
        colHeaderElement.setAttribute("role", "column-header");
        colHeaderElement.setAttribute(COLUMN_UID_ATTRIBUTE, col.uid);

        const colSortOrder = this.sortHelper.findSortOrder(col.uid);
        const columnSortInfo = this.sortHelper.findActiveColumn(col.uid);

        // Set column width
        const columnWidth = getSmartTableColumnWidth(this, col, this.options.columnsConfig);
        if (columnWidth) {
            colHeaderElement.style.width = columnWidth;
        }

        // create content
        const contentDiv = createHtmlElement("div", { cssClass: ["th-flex-content"] });
        colHeaderElement.append(contentDiv);

        // Set sortDiv
        const columnTitle = this.options.disableTitle ? "" : this.getColumnTitle(col, colHeaderElement);

        const alignmentClass = getSmartTableColumnAlignmentClass(col);
        const paddingClass = getSmartTableColumnAlignmentPaddingClass(col, this);

        let content: HTMLElement;
        if (typeof columnTitle === "object") {
            let tooltipOptions: Partial<Tooltip.Options> = { html: true };

            if (col.titleTooltip) {
                if (typeof col.titleTooltip === "object") {
                    tooltipOptions = { ...tooltipOptions, ...col.titleTooltip };
                } else {
                    tooltipOptions.title = col.titleTooltip;
                }
            }

            content = new CjsEllipsis(columnTitle, { tooltipOptions });
            content.classList.add(W_100, alignmentClass, paddingClass);
        } else {
            content = createHtmlElement("span", { cssClass: [TEXT_TRUNCATE, W_100, alignmentClass, paddingClass] });

            if (col.titleTooltip) {// Custom tooltip
                if (typeof col.titleTooltip === "string") {
                    content.title = col.titleTooltip;
                } else {
                    new Tooltip(content, col.titleTooltip);
                }
            } else {// Default tooltip
                content.title = DomTools.htmlStringToMultilineText(columnTitle);
            }
            appendHTMLElementContent(content, columnTitle);
        }

        const labelDiv = createHtmlElement("div", {
            cssClass: ["column-label", FLEX_CONTAINER, W_100, JUSTIFY_CONTENT.CENTER, ALIGN_ITEMS.CENTER],
            innerHTML: content
        });

        if (this.sortHelper.columnIsSortable(col.uid)) {
            setHtmlElementOptions(labelDiv, {
                cssClass: [SORTABLE_HEADER_CLASS],
                attributes: {
                    "aria-sort": columnSortInfo?.sortType ?? "none"
                }
            });
            labelDiv.addEventListener("click", mouseEvent => {
                this.sortHelper.onHeaderSort(mouseEvent, col);
            });
            this.sortHelper.addSortableDiv(labelDiv);
        }

        // Add class to always display sort "none"
        if (this.options.displaySortNone) {
            labelDiv.classList.add(DISPLAY_SORT_NONE);
        }

        if (columnSortInfo) {
            columnSortInfo.element = labelDiv;
        }

        // Div used to display the column sort order on multiple column sort
        const orderSortDiv = createHtmlElement("div", {
            attributes: {
                // Only set attribute if we have more than one sort
                "aria-sort-order": this.sortHelper.activeSortedColumns.length > 1 && colSortOrder > 0 ? colSortOrder.toString() : ""
            }
        });

        // Table header div with sort indicators
        const sortContentDiv = createHtmlElement("div", {
            cssClass: [TABLE_HEADER_CLASS],
            innerHTML: [labelDiv, orderSortDiv]
        });

        // retrieve the div element in which toggle btn will be added
        if (params?.displayToggleIconOnHeader) {
            sortContentDiv.prepend(this.hierarchyHelper.toggleHierarchyBtn);
        }

        contentDiv.append(sortContentDiv);

        // Filter
        if (this.filterHelper.columnIsFilterable(col)) {
            const filter = this.filterHelper.filters.find(f => f.column.uid === col.uid);
            if (!filter) {
                this.filterHelper.filters.push({ column: col, value: "" });
            }

            // Set searchInput
            const searchInputOptions: Partial<InputOptions<string>> = {
                inputType: "text",
                enableFormControl: true,
                placeholderText: this.filterHelper.getColumnFilterPlaceholder(col),
                enableClearButton: this.options.filtersEnableClearButton
            };
            if (filter) {
                searchInputOptions.value = filter.value;
            }
            const searchInput = new CjsInput<string>(searchInputOptions);
            searchInput.input.classList.add(SEARCH_FILTER, TEXT_TRUNCATE, FILTERABLE_HEADER_CLASS);
            col.filterInput = searchInput;

            searchInput.inputEvent.on((e) => { this.filterHelper.onFilterInput(col, searchInput, e); });
            searchInput.clearEvent.on((e) => { this.filterHelper.onFilterInput(col, searchInput, e); });
            searchInput.input.addEventListener(REFRESH_FILTER, (e) => { this.filterHelper.onFilterInput(col, searchInput, e); });

            // Display filtered icon in the header label
            // TODO see if propertyName can be replaced by column uid
            if (this.filterHelper.isExcelLikeFiltered(col.propertyConfig?.propertyName ?? col.uid) || this.filterHelper.isAdvancedFiltered(col.propertyConfig?.propertyName)) {
                const filterIcon = this.filterHelper.getFilterIcon(col.uid);
                if (filterIcon) {
                    labelDiv.after(filterIcon);
                }

                contentDiv.classList.add(FILTERED_COLUMN);
            }

            if (this.filterEnterKeyEvent.onWasCalled) {
                // if we listen to filter enter, we add a listener to trigger filterEnterKeyEvent event
                searchInput.input.addEventListener("keydown", (e: Event) => {
                    if ((e as KeyboardEvent).key === KeyboardInput.ENTER) {
                        this.filterHelper.onFilterEnterPressed(col, searchInput, e as KeyboardEvent);
                    }
                });
            }

            // Add to searchDiv
            const searchDiv = createHtmlElement("div", { cssClass: [PADDING.P_1, "filter-div"] });
            searchDiv.append(searchInput);

            // Add to header
            contentDiv.append(searchDiv);
        }
        return colHeaderElement;
    }

    /**
     * Render column TH
     */
    protected _renderColumnHeader(col: SmartTableColumn<ItemData>, params?: { displayToggleIconOnHeader?: boolean }): HTMLElement {
        return this.defaultRenderColumnHeader(col, params);
    }

    /**
     * Fill table header with columns
     */
    public renderHeader(renderCacheData: RenderCacheData): void {
        // set header visibility
        toggleVisible(this.components.tableHead, !this.options.disableHeader, Display.TABLE_HEADER_GROUP);

        // Reset tableHeader
        empty(this.components.tableHead);
        this.sortHelper.resetSortableDivs();

        if (!this.options.disableHeader) {

            // The fastest way to go through the native JavaScript DOM API, while constructing your table, is within a document fragment
            const fragment = document.createDocumentFragment();

            // add toggle btn if toggle option enable
            let columnToDisplayToggleArrowOn: SmartTableColumn<ItemData> | undefined = undefined;

            if (this.options.hierarchyOptions?.enableToggleButton) {
                columnToDisplayToggleArrowOn = getFirstColumn(this.options.columns, this.options.columnsConfig);
            }

            for (const col of getVisibleColumnsFromConfiguration(this.options.columns, this.options.columnsConfig)) {
                const displayToggleIconOnHeader = col.uid === columnToDisplayToggleArrowOn?.uid;
                const colHeaderElement = this._renderColumnHeader(col, { displayToggleIconOnHeader });
                col.headerHtml = colHeaderElement;

                fragment.append(colHeaderElement);

                // Create a resizer div to have a resizable column / both global and column options must be enabled
                if (isSmartTableColumnResizable(this, col)) {
                    const resizer = createSmartTableResizerColumn(this, col);
                    this._resizerColumnDivList.push(resizer);
                }
            }


            // add "fill" column
            if (this.options.addFillColumn) {
                const fillColHeaderElement = this._renderColumnHeader(_fillColumn);
                fillColHeaderElement.classList.add(W_100);
                fragment.append(fillColHeaderElement);
            }

            const tableRowElement = createHtmlElement("tr", { cssClass: ROW_HEADER_CLASS, innerHTML: fragment });
            this.components.tableHead.append(tableRowElement);
        }

    }

    /** Render table footer */
    public renderFooter(renderCacheData: RenderCacheData): void {
        const withFooterInfo = Boolean(this.options.enableFooterInfo);
        const hasStickyFooterRow = this.stickyTableRows.some(r => r.sticky?.stickyType === "footer");
        const withFooter = this.options.enableFooter || this.options.renderFooter != null || withFooterInfo || hasStickyFooterRow;
        toggleClass(this.components.table, withFooter, "with-footer");
        toggleClass(this.components.footerSuffix, hasStickyFooterRow, "with-sticky");
        toggleVisible(this.components.tableFooter, withFooter, Display.TABLE_FOOTER_GROUP);

        if (this.options.renderFooter) {
            this.options.renderFooter(this.components.tableFooter, renderCacheData);
        } else if (this.options.enableFooter) {
            empty(this.components.tableFooter);

            const tableRowElement = document.createElement("tr");

            for (const col of getVisibleColumnsFromConfiguration(this.options.columns, this.options.columnsConfig)) {
                const tableFooterElement = document.createElement("th");
                if (col.sticky) {
                    addStickyColumn(this, col, tableFooterElement);
                }
                tableFooterElement.setAttribute("role", "columnfooter");
                tableFooterElement.setAttribute(COLUMN_UID_ATTRIBUTE, col.uid);

                // Create content
                const contentDiv = createHtmlElement("div", { cssClass: ["th-flex-content"] });
                tableFooterElement.append(contentDiv);

                const ellipsisText = new CjsEllipsis(this._getColumnFooter(col, tableFooterElement), { tooltipOptions: { html: true } });
                ellipsisText.classList.add(W_100);

                // Set footerDiv
                const footerDiv = createHtmlElement("div", {
                    cssClass: [TABLE_FOOTER_CLASS, "column-label", FLEX_CONTAINER_CENTERED, W_100],
                    innerHTML: ellipsisText
                });
                contentDiv.append(footerDiv);

                col.footerHtml = tableFooterElement;
                tableRowElement.append(tableFooterElement);
            }
            this.components.tableFooter.append(tableRowElement);
        } else {
            empty(this.components.tableFooter);
        }

        this.renderFooterInfo();
    }

    /** 
     * Render table footer info
     */
    public renderFooterInfo(): void {
        if (!Boolean(this.options.enableFooterInfo)) { return; }

        const content = this.styleHelper.getFooterInfoContent();
        this.components.tableFooter.append(content.tr);

        // Refresh content and position
        this.styleHelper.refreshFooterInfoDisplay();
    }
    // #endregion


    /**
     * Call this function when you delete an item. It checks if we delete an item at the end of the list change index to get correct display on next refresh
     */
    public itemDeleted(): void {
        if (this.options.firstDisplayedItemIndex > 0) {// TODO BGS review
            this.options.setWithoutRefresh({ firstDisplayedItemIndex: Math.max(0, this.options.firstDisplayedItemIndex - 1) });
        }
    }

    /** Sort columns by 'order' field */
    public sortColumns(): SmartTableColumn<ItemData>[] {
        return sortFields(this.options.columns, "order");
    }

    /**
     * Set selection (cell and row) and dispatch linked events
     */
    public dispatchRowSelected(
        cell: SmartTableCell<ItemData>,
        selectionOptions?: { event?: MouseEvent | TouchEvent, origin?: "click" | "key" },
        options?: { keepSelection?: boolean }
    ): void {
        // Sticky rows can't be selected
        if (cell.row.sticky) { return; }

        // Used to trigger selectionChangeEvent if needed
        let selectionHasChanged = false;

        const selectionModel = this.getSelectionModel();
        const rowUid = cell.row.uid;
        const isShift = Boolean(selectionOptions?.event?.shiftKey);
        const isCtrl = Boolean(selectionOptions?.event?.ctrlKey);

        /**
         * Behavior : enable selection on row click, except when checkbox column have "rowSelectionWithCheckboxOnly" property
         * In this case, allow selection only when clicking on said checkbox (not on row)
         */
        const checkboxColumnHandledResult = selectionModel.handleRowSelectionWithCheckboxOnly(cell, selectionOptions?.event);
        selectionHasChanged = checkboxColumnHandledResult.selectionHasChanged;

        if (!checkboxColumnHandledResult.handled && !selectionHasChanged) {
            // Default behavior
            if (this.isSelectableRow(cell.row) && !options?.keepSelection) {
                selectionHasChanged = this._selectionModel.select(rowUid, { isShift, isCtrl });
            }
        }

        if (selectionOptions?.origin === "click") {
            this.editionHelper.setFocusCell(cell, "click"); // TODO BGS faire une vraie gestion du lead selection et du focus
            this.rowClickEvent.emit({
                cell,
                clickEvent: selectionOptions.event
            });
        }
    }

    /**
     * get the row item data and keep value in a cache to avoid multiple calls to VOF function
     * @returns the row item data or null if none
     */
    public getRowItemData(row: SmartTableRow<ItemData>): ItemData | null {
        if (row.__rowItemDataCache === undefined) {
            row.__rowItemDataCache = getVOFValue(row.data) ?? null;
        }
        return row.__rowItemDataCache;
    }

    /**
     * Compute the cell value from column or rowItemData and updates cell.value
     */
    public getCellValue(cell: SmartTableCell<ItemData>, renderCacheData?: RenderCacheData): anyReallyAny {

        let cellValue: anyReallyAny;
        if (cell.column.getCellValue) {
            cellValue = cell.column.getCellValue(cell, renderCacheData as Record<string, unknown> | undefined);
        } else {
            cellValue = cell.rowItemData && cell.column.propertyConfig ? getProp(cell.rowItemData, cell.column.propertyConfig.propertyName) : null;// TODO use "BGS new DataModel" once available
        }

        cell.value = cellValue;
        return cellValue;
    }

    /** Render content of tr row
     * @param cell smartable cell
     * @param renderCacheData render cache data
     */
    public renderTdCellElement(cell: SmartTableCell<ItemData>, renderCacheData: RenderCacheData | undefined, last?: boolean): SmartTableCell<ItemData> {

        const tdElement = createHtmlElement("td");
        if (cell.column.sticky) {
            addStickyColumn(this, cell.column, tdElement);
        }
        cell.html = tdElement;

        tdElement.setAttribute(COLUMN_UID_ATTRIBUTE, cell.column.uid);

        const columnWidth = cell.column.width;
        if (columnWidth) {
            tdElement.style.width = columnWidth;
        }

        // keep focus cell on refresh
        this.editionHelper.refreshFocusCellIfSame(cell);

        // col renderCell overrides the options
        let renderer: { renderCell?: SmartTableCellRenderer<ItemData> } = cell.column;
        if (!cell.column.renderCell) {
            renderer = this.options;
        }
        const renderResult = renderer.renderCell?.(cell, renderCacheData);

        // Use cellRenderer if set for this cell, fill cell content
        if (renderResult != null) {
            this._defaultCellBuilder({ cell, tdElement, renderResult, renderCacheData }, undefined);
        } else {
            // Use PropertyConfig (or not defined: cell.displayString, cell.value) to fill cell content
            this._fillTdElementFromPropertyConfig(cell, tdElement, renderCacheData);
        }

        // Apply styling to cell
        this._handleCellStyling(tdElement, cell, renderCacheData);

        if (this.options.hierarchyOptions) {

            if (this.hierarchyHelper.matchHierarchyColumn(cell)) {
                // Add collapse button
                const isCollapseBtnAdded = this._addCollapseButtonIfNeeded(tdElement, cell);

                this._addHierarchySection(tdElement, cell, isCollapseBtnAdded);
            }


            // Add select children if option activated
            if (this.options.hierarchyOptions?.selectChildren && (this.options.hierarchyOptions.columnUid ? cell.column.uid === this.options.hierarchyOptions.columnUid : last)) {
                this._addSelectChildren(tdElement, cell);
            }
        }


        if (renderCacheData == null) {
            // if this function is not called from _populateRow, we may need to clear the cells cache (for example on cellEditionFinishEvent)
            this._clearCellsCache();
        }
        return cell;
    }

    /**
     * Add targets to watch the smartTable scroll.
     * @param targets htmlElements or their ref attribute
     * @returns 
     */
    public addScrollTargets(targets: HTMLElement[] | string[]): void {
        return this.scrollBar.addScrollTargets(targets);
    }

    /**
     * @returns the editor for the provided cell
     */
    public getCellEditor(cell: SmartTableCell<ItemData>): CjsEditor<anyReallyAny> | undefined {
        const specificGetCellEditor = cell.column.getCellEditor ?? this.options.getCellEditor;
        let editor = specificGetCellEditor?.(cell);
        if (!editor) {// if no editor defined, return default one
            editor = this.defaultGetCellEditor(cell);
        }
        return editor;
    }

    /** Default GetCellEditor - Based the EditorFactory service same as GeneratedForm */
    public defaultGetCellEditor = (cell: SmartTableCell<ItemData>): CjsEditor<anyReallyAny> | undefined => {
        if (cell.column.propertyConfig) {
            const editor = this._editorFactory.newEditor(cell.column.propertyConfig);

            // For CheckboxEditor, always center checkbox in component
            if (editor instanceof CjsCheckboxEditor) {
                editor.checkboxOptions.setWithoutRefresh({ checkboxHorizontalAlignment: JUSTIFY_CONTENT.CENTER });
            }

            return editor;
        }
        return undefined;
    };

    /**
     * Get the cell from row and column
     * If cell isn't in cache, 
     */
    public getCellByRowAndColumn(rowUid: RowUid, colUid: ColumnUid, renderCacheData?: RenderCacheData): SmartTableCell<ItemData> | undefined {
        let cell = this.cellsByRowAndColumn[rowUid]?.[colUid];
        if (!cell) {
            const row = this.rowsByUid.get(rowUid);
            const column = this.colsByUid.get(colUid);
            if (row && column) {
                cell = this.buildCell(row, column, renderCacheData);
            }
        }
        return cell;
    }

    /**
     * Get the cell from row and column objects
     * builds the cell with provided options if not present in cache
     */
    public getCellByRowAndColumnObjects(row: SmartTableRow<ItemData>, col: SmartTableColumn<ItemData>, renderCacheData?: RenderCacheData, options?: { doNotRegisterCellInCache?: boolean }): SmartTableCell<ItemData> {
        let cell = this.cellsByRowAndColumn[row.uid]?.[col.uid];
        if (!cell) {
            cell = this.buildCell(row, col, renderCacheData, options);
        }
        return cell;
    }

    //#region Sort

    /** 
     * Clear smartTable configuration
     * Default: refresh is not called after clear
     */
    public clearConfiguration(p: SmartTableClearConfigurationParams & {
        refresh?: boolean
    }): void {
        if (p.filters) {
            this.filterHelper.filters = [];

            // Don't forget to clear excelLikeSortFilter cache
            this.filterHelper.excelLikeSortFilter?.clear();
        }

        if (p.sort) {
            this.sortHelper.resetAriaSort();
            this.sortHelper.resetActiveSortedColumns();
        }

        if (p.hierarchy) {
            this.hierarchyHelper.clear();
        }

        if (p.refresh) {
            this.refresh("clearConfiguration");
        }
    }

    /**
     * Display row if
     * - a descendant is displayed (all ancestors should be displayed too) or row is displayed
     * - and none of its ancestors is collapsed
     */
    public shouldDisplayRow(row: SmartTableRow<ItemData>): boolean {
        if (!row.hierarchyInfo) {
            return row.displayed;
        } else {
            return (row.hierarchyInfo.isInFlatList || row.displayed) && !row.hierarchyInfo.ancestorCollapsed;
        }
    }
    //#endregion

    /** Refresh tableScrollBar */
    protected _refreshScrollbar(): void {
        if (this.isSmart()) {
            this._refreshHeaderSuffixHeight();
            this._refreshStickyHeaderSuffixHeight();
            // there is no this._refreshStickyFooterSuffixHeight(); because sticky footer in integrated within footer and handled below by _refreshFooterSuffixHeight
            this._refreshFooterSuffixHeight();
            this._refreshScrollShadows();

            // Display scrollbar if at least one row
            toggleVisible(this.components.scrollbarContainer, this._displayableRows.length > 1, Display.FLEX);

            if (this.displayedRows.length >= this._displayableRows.length && this._availableHeight >= 0 && !this.options.allowScrollUntilLastRow) {
                // all rows displayed and still have room
                setDisplay(this.scrollBar, Display.NONE);
            } else {

                setDisplay(this.scrollBar, Display.BLOCK);

                const lastDisplayedRow = this.displayedRows[this.displayedRows.length - 1];
                let truncatedRatio = 0;// compute the visible ratio of the last displayed row
                if (this._availableHeight < 0) {
                    const lastRowHeight = lastDisplayedRow?.height;
                    if (lastRowHeight && lastRowHeight > 0) {
                        truncatedRatio = Math.min(1, -this._availableHeight / lastRowHeight);
                    }
                }

                this.scrollBar.options.setWithoutRefresh({
                    firstDisplayedIndex: this.options.firstDisplayedItemIndex,
                    totalElementsCount: this._displayableRows.length,
                    selectedElementsCount: this.options.allowScrollUntilLastRow ? 1 : this.displayedRows.length - truncatedRatio
                });

                // always refresh
                // we need to provide scrollBarHeight because sometimes, the scrollbar clientHeight is not yet valid at the time we call refresh (bug found in the editList)
                this.scrollBar.refresh("smartTable", this._bodyVisibleHeight);

                if (this.scrollBar.shouldBeHidden()) {
                    // this should not happen... since _availableHeight is supposed to handle this
                    setDisplay(this.scrollBar, Display.NONE);
                }

                // update suffix background
                const firstColHeader = getFirstColumn(this.options.columns, this.options.columnsConfig)?.headerHtml;
                if (firstColHeader) {
                    const firstTdStyle = getComputedStyle(firstColHeader);// TODO try to avoid getComputedStyle
                    const backgroundColor = firstTdStyle.getPropertyValue("background-color");
                    this.components.headerSuffix.style.backgroundColor = backgroundColor;

                    // Apply on footer is enabled
                    if (this.options.enableFooter) {
                        this.components.footerSuffix.style.backgroundColor = backgroundColor;
                    }
                }
            }
        } else {
            // standard scrollbar
            setDisplay(this.scrollBar, Display.NONE);
        }
        toggleClass(this.components.smartTableContainer, this.options.scrollBar === "standard", "standard-scrollbar");
    }

    /** Refresh height of header suffix, useful for correct smartScrollBar display */
    protected _refreshHeaderSuffixHeight(): void {
        fastdom.measure(() => {
            const headHeight = this.components.tableHead.offsetHeight;
            fastdom.mutate(() => {
                this.components.headerSuffix.style.height = headHeight + "px";
            });
        });
    }

    /** Refresh height of footer suffix, useful for correct smartScrollBar display */
    protected _refreshFooterSuffixHeight(): void {
        fastdom.measure(() => {
            const footerHeight = this.components.tableFooter.offsetHeight;
            fastdom.mutate(() => {
                this.components.footerSuffix.style.height = toPx(footerHeight + this.getHorizontalScrollbarHeight());
            });
        });
    }

    /** Refresh height of sticky header suffix, useful for correct smartScrollBar display */
    protected _refreshStickyHeaderSuffixHeight(): void {
        fastdom.mutate(() => {
            this.components.stickyHeaderSuffix.style.height = this._totalStickyHeaderRowsHeight + "px";
        });
    }

    /** Init listeners of smartTable */
    protected _initListeners(): void {

        // Listen for list click event
        this.components.tableBody.addEventListener("click", this._onSingleClickTableBodyRow.bind(this));
        this.components.tableBody.addEventListener("dblclick", this._onDoubleClickTableBodyRow.bind(this));

        // Listen for smartphone devices tap
        const ham = new Hammer(this.components.table!, { inputClass: Hammer.TouchInput });
        ham.on("tap", (e: HammerInput) => {
            if (e.target.getAttribute(REF_ATTRIBUTE) !== HEADER_CHECKBOX_INPUT) { // Do not prevent or stop propagation if event is on selectAll ck
                // On mobile we had to prevent touchstart from triggering a click (preventDefault)
                // to allow scrolling with smartscrollbar, this handler restores the expected behavior
                e.srcEvent.stopPropagation();
                e.srcEvent.preventDefault();
                this._onSingleClickTableBodyRow(e);
                // TODO handle double tap (dblclick like)
            }
        });
        this._elementsToDispose.hammer = ham;

        // On scrollbar scrolling event
        this.scrollBar.refreshIndexEvent.on((e) => {
            const firstDisplayedItemIndexBefore = this.options.firstDisplayedItemIndex;
            this.options.setWithoutRefresh({ firstDisplayedItemIndex: e.detail.index });

            const scrollIncrement: number = firstDisplayedItemIndexBefore - this.options.firstDisplayedItemIndex;
            if (scrollIncrement !== 0) {
                // Emit event
                this.scrollEvent.emit({
                    index: this.options.firstDisplayedItemIndex,
                    scrollIncrement
                });
                this.render("scroll", { scrollIncrement });
            }
        });

        if (!this.options.doNotObserveResize) {
            // On resize, we have to render again to check the correct number of lines we can display
            const resizeObserver = new ResizeObserver(entries => {
                ignoreResizeObserverLoopLimit(entries, () => {
                    if (this.offsetParent !== null && this.isInitDone && !this.editionHelper.isEditState()) {
                        // Test if element is displayed, offsetParent is null when element is hidden (inside display none div for example), https://developer.mozilla.org/fr/docs/Web/API/HTMLElement/offsetParent
                        if (this.lastRenderBounds?.smartTable.height !== this.clientHeight) {// changing width does not trigger render
                            this.render("resize");
                        }
                    }
                });
            });
            resizeObserver.observe(this);
            this._elementsToDispose.resizeObserver = resizeObserver;
        }

        this.selectionChangeEvent.on(this.hierarchyHelper.refreshSelectChildrenContent.bind(this.hierarchyHelper));
    }

    /** Add listeners when smartTable is added to DOM */
    public connectedCallback(): void {
        // Add click listener on document, to finish cell edition if current focusedCell isn't clicked
        document.addEventListener("click", this.editionHelper.onDocumentClickFinishCellEdition);
        // Add click listener on document, to disable focusCell if smartTable is not more focused
        document.addEventListener("click", this.editionHelper.onDocumentClickUpdateFocusCell);

        // Add keydown listener on document, to listen to "ctrl + A" when smartTable is focused
        this.addEventListener("keydown", this._selectAllFromKeyboard);
    }

    /** Remove listeners when smartTable is detached / removed from DOM */
    public disconnectedCallback(): void {
        // Remove listener if table is detach from DOM
        if (this.editionHelper) {// editionHelper could be set to undefined in dispose
            document.removeEventListener("click", this.editionHelper.onDocumentClickFinishCellEdition);
            document.removeEventListener("click", this.editionHelper.onDocumentClickUpdateFocusCell);
        }

        // Remove keydown listener
        this.removeEventListener("keydown", this._selectAllFromKeyboard);

        // Uninstall search helper on table
        this.searchHelper.uninstall();
    }

    //#endregion

    /**
     * @returns true if last row is displayed
     */
    public isLastRowDisplayed(): boolean {

        if (this._availableHeight < 0) {
            // last row is truncated
            return false;
        }

        const result = this.options.firstDisplayedItemIndex + this.displayedRows.length >= this._displayableRows.length;
        return result;
    }

    /**
     * on click callback
     */
    protected _onSingleClickTableBodyRow(e: MouseEvent | HammerInput): void {
        this._onClickTableBodyRow(e, "simple");
    }

    /**
     * on dblClick callback
     */
    protected _onDoubleClickTableBodyRow(e: MouseEvent | HammerInput): void {
        this._onClickTableBodyRow(e, "double");
    }

    /**
     * OnClickTableBody
     */
    protected _onClickTableBodyRow(e: MouseEvent | HammerInput, type: "simple" | "double"): void {
        const element = e.target as HTMLElement;
        const rowClicked = element.closest("tr");
        const tdClicked = element.closest("td");

        if (rowClicked && tdClicked) {

            // Retrieve cell clicked
            const rowUid = rowClicked.getAttribute(ROW_UID_ATTR) ?? "";
            const columnUid = tdClicked.getAttribute(COLUMN_UID_ATTRIBUTE) ?? "";
            const cellClicked = this.getCellByRowAndColumn(rowUid, columnUid, undefined);

            if (cellClicked) {
                const event = e instanceof MouseEvent ? e : e.srcEvent;

                // Edit value if needed && allowed
                this.editionHelper.editValueIfNeeded(cellClicked, { click: type });

                if (!this.options.columns.find(c => c.uid === columnUid)?.selectCallbackDisabled) {
                    this.dispatchRowSelected(cellClicked, { event, origin: "click" });
                }
            }
        }
    }

    /**
     * Removes the row from displayedRows and from the DOM
     * @param displayedRowIndex the index of the row to remove
     */
    protected _removeDisplayedRow(displayedRowIndex: number): void {
        const displayedRow = this.displayedRows.splice(displayedRowIndex, 1)[0];
        if (displayedRow?.tr) {
            // remove row from table
            displayedRow.tr.remove();
        }

        // Remove child row if exists
        if (displayedRow?.childTr) {
            displayedRow.childTr.remove();
        }
    }

    /**
     * populate row cells
     */
    protected _populateRowCells(rowTr: HTMLTableRowElement, row: SmartTableRow<ItemData>, params: { renderCacheData: RenderCacheData }): void {

        const visibleColumns = getVisibleColumnsFromConfiguration(this.options.columns, this.options.columnsConfig);
        if (this.options.populateRowCells?.(rowTr, row, { ...params, visibleColumns })) {
            // row was populated
            return;
        }

        // Loop through columns
        for (let i = 0; i < visibleColumns.length; i++) {
            const col = visibleColumns[i];
            const last = i === visibleColumns.length - 1;
            const cell = this.renderTdCellElement(this.buildCell(row, col, params.renderCacheData), params.renderCacheData, last);
            if (cell.html) {
                rowTr.append(cell.html);
            }
        }
        // add fill column cell
        if (this.options.addFillColumn) {
            const cell: SmartTableCell<ItemData> = { row, column: _fillColumn, rowItemData: this.getRowItemData(row), value: undefined };
            this.renderTdCellElement(cell, params.renderCacheData);
            if (cell.html) {
                rowTr.append(cell.html);
            }
        }
    }

    /**
     * Build a smartTable cell
     * Will register cell created in cellsByRowAndColumn cache (if options.doNotRegisterCellInCache is false)
     */
    public buildCell(row: SmartTableRow<ItemData>, column: SmartTableColumn<ItemData>, renderCacheData?: RenderCacheData, options?: { doNotRegisterCellInCache?: boolean }): SmartTableCell<ItemData> {

        const cell: SmartTableCell<ItemData> = { row, column, rowItemData: this.getRowItemData(row), value: undefined };
        // Actualize cell value
        // Compute cell value        
        this.getCellValue(cell, renderCacheData);

        if (!options?.doNotRegisterCellInCache) {
            // register cell
            const cellsByColumn = this._cellsByRowAndColumn[row.uid] ??= {};
            cellsByColumn[column.uid] = cell;
            this._clearCellsCache();// Clear cells cache to force reset
        }

        return cell;
    }

    /**
     * Create a rowDiv element and set its inner html with provided formatter
     * @param row to display
     * @param params
     * @param params.renderCacheData record passed to all getCellValue calls during a single render other table that allows application to store tmp data
     */
    protected _populateRow(row: SmartTableRow<ItemData>, params: { renderCacheData: RenderCacheData }): void {

        // init row if needed (html, height and cellsByColumnUid)
        if (row.refreshUid !== this._refreshUid || !row.tr || row.height == null || this.cellsByRow[row.uid] == null) {
            row.refreshUid = this._refreshUid;

            // build tr
            const rowTr = createHtmlElement("tr");

            const rowHeight = this.getRowHeight(row);
            row.height = rowHeight;
            rowTr.style.height = rowHeight + "px";

            if (!this.options.fitContent) {
                rowTr.style.flex = "0 0 auto";
            }
            if (row.sticky) {
                rowTr.classList.add(STICKY_ROW_CLASS);
            }

            // Set row uid
            rowTr.setAttribute(ROW_UID_ATTR, row.uid);

            // Check if row background display can be added
            let canEnableBackgroundDisplay = false;
            const enableBackgroundDisplay = this.options.selection?.enableBackgroundDisplay;
            if (enableBackgroundDisplay) {
                if (typeof enableBackgroundDisplay === "function") {
                    canEnableBackgroundDisplay = enableBackgroundDisplay(row);
                } else {
                    canEnableBackgroundDisplay = enableBackgroundDisplay;
                }
            }

            // Add selected row class
            if (row.selected) {
                rowTr.classList.add(TABLE_ROW_SELECTED);
                if (canEnableBackgroundDisplay) {
                    rowTr.classList.add(HIGHLIGHT_TABLE_ROW_SELECTED);
                }
            }
            // Store row HTML element
            row.tr = rowTr;

            if (this.shouldDisplayRow(row)) {

                if (!row.sticky && this.isSelectableRow(row) || this.options.isCellEditable) {
                    rowTr.classList.add(CLICKABLE_ROW_CLASS);
                }

                this._populateRowCells(rowTr, row, params);
            }

            // Add theses classes only if hierarchy is enable
            if (row.hierarchyInfo) {

                // Add class to represent collapsed state
                const isParent = Boolean(row.hierarchyInfo?.isParent);
                if (isParent) {
                    const isRowsCollapsed = Boolean(row.hierarchyInfo?.collapsed);
                    toggleClass(rowTr, isRowsCollapsed, "row-collapsed");
                    toggleClass(rowTr, !isRowsCollapsed, "row-expanded");
                }

                // Add class to represent row state
                toggleClass(rowTr, isParent, "hierarchy-parent-row");
                toggleClass(rowTr, !isParent, "hierarchy-child-row");
            }
        }
        this._clearCellsCache();
    }

    /**
     * Remove the empty row if it is present
     */
    protected _removeEmptyRow(): void {
        if (this._isAdditionalEmptyRowInDom && this._additionalEmptyRow) {
            this._additionalEmptyRow.remove();
            this._isAdditionalEmptyRowInDom = false;
        }
    }

    /**
     * Adds the empty row if not already there
     */
    protected _appendEmptyRow(): void {
        const visibleColumnsLength = getVisibleColumnsCount(this.options.columns, this.options.columnsConfig, this.options.addFillColumn);
        if (this._additionalEmptyRow == null) {
            const emptyTr = createHtmlElement("tr", { cssClass: [EMPTY_ROW_CLASS, H_100] });
            const tdElement = createHtmlElement("td", { cssClass: [H_100], attributes: { colspan: String(visibleColumnsLength) } });
            emptyTr.append(tdElement);
            this._additionalEmptyRow = emptyTr;
        }
        if (!this._isAdditionalEmptyRowInDom) {
            this._isAdditionalEmptyRowInDom = true;
            this._additionalEmptyRow.children.item(0)?.setAttribute("colspan", String(visibleColumnsLength));
            this.components.tableBody.append(this._additionalEmptyRow);
        }
    }

    /** 
     * Append row to DOM 
     * @param row the row to insert
     * @param params
     * @param params.insertAfter indicates if we want to add the element at the bottom of the list or after another element or at the top (undefined)
     * @param params.insertIndex the index where row will be inserted in _displayedRows
     */
    protected _appendRow(row: SmartTableRow<ItemData>, params: { insertAfter?: HTMLElement | "bottom", insertIndex: number }): void {
        if (!row.tr) { return; }

        // Add drag and drop handler icon if needed
        this.dragAndDropHelper.refreshCellDragHandler(row);

        if (this.options.customizeRowBeforeAppend) {
            this.options.customizeRowBeforeAppend(row);
        }

        // console.info("append", row.globalIndex, "at", params.insertIndex);
        if (params.insertAfter === undefined) {
            // insert after not defined => insert at top of list
            this.components.tableBody.prepend(row.tr);
            if (row != null) {
                this._displayedRows.unshift(row);
            }
        } else {
            if (params.insertAfter === "bottom") {
                // insert at bottom of list
                this.components.tableBody.append(row.tr);
                this._displayedRows.push(row);
            } else {
                // insert after provided element
                params.insertAfter.after(row.tr);
                this._displayedRows.splice(params.insertIndex, 0, row);
            }
        }

        if (this.options.customizeRowAfterAppend) {
            this.options.customizeRowAfterAppend(row);
        }

        // Insert child after the row if exists
        if (row.childTr && this.hierarchyHelper.canInsertRowChild(row)) {
            const td = row.childTr.children?.[0] as HTMLTableCellElement | undefined;
            if (!td) { return; }

            const tdChildren = td.children?.[0] as HTMLDivElement | undefined;
            if (!tdChildren) { return; }

            // if parent has a height constraint, rows height is limited
            if (!this.options.parentContainerHasNoHeightConstraint) {
                let childRowAvailableHeight: number;
                const rowParentHeight = row.height ?? DEFAULT_SMART_TABLE_ROW_HEIGHT_VALUE;
                const lastDisplayableRowIndex = this._displayableRows[this._displayableRows.length - 1].globalIndex;

                // If row is the last row displayed, don't display other row under parent row
                if (row.globalIndex === lastDisplayableRowIndex) {
                    childRowAvailableHeight = rowParentHeight;
                } else { // If row isn't the last row displayed, we keep available height for parent row and next row
                    const displayableRowIndex = indexOf(this._displayableRows, row);
                    const nextRowHeight = this._displayableRows[displayableRowIndex + 1].height ?? DEFAULT_SMART_TABLE_ROW_HEIGHT_VALUE;
                    childRowAvailableHeight = rowParentHeight + nextRowHeight;
                }

                // Set maxHeight to the div container of expanded content
                setHTMLElementStyle(tdChildren, { maxHeight: toPx(this._bodyVisibleHeight - childRowAvailableHeight) });
            }

            row.tr.after(row.childTr);
        }
    }

    /** 
     * Compute bounds of all components of smartTable 
     * ! avoid using this function since it forces a reflow
     */
    public computeTableBounds(): SmartTableBounds {
        // Update last render bounds
        this.lastRenderBounds = {
            uid: this._refreshUid,
            smartTable: this.getBoundingClientRect(),
            table: this.components.table.getBoundingClientRect(),
            body: this.components.tableBody.getBoundingClientRect(),
            footer: this.components.tableFooter.getBoundingClientRect(),
            header: this.components.tableHead.getBoundingClientRect()
        };
        return this.lastRenderBounds;
    }

    /**
     * @returns the cached renderBounds if still valid, computes new values otherwise
     */
    public getTableBounds(): SmartTableBounds {
        if (this.lastRenderBounds && this.lastRenderBounds.uid === this._refreshUid) {
            return this.lastRenderBounds;
        }
        return this.computeTableBounds();
    }

    /**
     * @returns the sum of all sticky rows
     */
    protected _computeTotalStickyRowsHeight(): { headerHeight: number, footerHeight: number } {
        let totalHeader = 0;
        let totalFooter = 0;
        for (const stickyRow of this._stickyTableRows) {
            if (stickyRow.sticky?.stickyType === "header") {
                totalHeader += stickyRow.height ?? 0;
            } else {
                totalFooter += stickyRow.height ?? 0;
            }
            if (stickyRow.resizerTr) {
                if (stickyRow.sticky?.stickyType === "header") {
                    totalHeader += this.options.stickyRowResizerHeight;
                } else {
                    totalFooter += this.options.stickyRowResizerHeight;
                }
            }
        }
        return { headerHeight: totalHeader, footerHeight: totalFooter };
    }

    /**
     * @returns true if resize is disabled for sticky row
     */
    public isStickyRowResizeDisabled(row: SmartTableRow<ItemData>): boolean {
        if (row.sticky == null) { return true; }
        if (row.sticky.disableResize) { return true; }
        if (this.options.disableStickyRowsResize === true) { return true; }
        if (this.options.disableStickyRowsResize != null && this.options.disableStickyRowsResize === row.sticky.stickyType) { return true; }
        return false;
    }

    /** 
     * Append sticky row to DOM 
     * @param row row to append to either table body
     */
    protected _appendStickyRow(row: SmartTableRow<ItemData>): void {
        if (row.sticky && row.tr && row.height != null) {

            row.tr.style.height = row.height + "px";

            if (!this.isStickyRowResizeDisabled(row) && row.resizerTr == null) {
                // initialize resizeTr
                const resizerRow = createHtmlElement("tr", {
                    attributes: {
                        [ROW_UID_ATTR]: row.uid
                    },
                    cssClass: ["position-relative", "sticky-row-resizer"],
                    style: {
                        height: this.options.stickyRowResizerHeight + "px"
                    }
                });
                row.resizerTr = resizerRow;

                const tdElement = createHtmlElement("td");

                const visibleColumnsLength = getVisibleColumnsCount(this.options.columns, this.options.columnsConfig, this.options.addFillColumn);
                tdElement.setAttribute("colspan", String(visibleColumnsLength));
                resizerRow.append(tdElement);

                resizerRow.addEventListener("mousedown", this.onStickyRowResizerMouseDown, false);
            }

            if (row.sticky?.stickyType === "footer") {
                if (row.resizerTr != null) {
                    // append resize row
                    this.components.tableFooter.append(row.resizerTr);
                }
                this.components.tableFooter.append(row.tr);
            } else {
                this.components.tableBody.append(row.tr);
                if (row.resizerTr != null) {
                    // append resize row
                    this.components.tableBody.append(row.resizerTr);
                }
            }
        }
    }

    /**
     * Handle column <=> cell styling
     * @param tdElement to add styling
     * @param cell to apply cell
     */
    protected _handleCellStyling(tdElement: HTMLElement, cell: SmartTableCell<ItemData>, renderCacheData: RenderCacheData | undefined): void {
        // Apply cellStyle to each cell

        const style = cell.column.getCellStyle?.(cell, renderCacheData) ?? this.options.getCellStyle?.(cell, renderCacheData);
        if (style) {
            for (const cssAttribute in style) {
                const getCssValue = style[cssAttribute];

                if (getCssValue && cell.value !== null) {
                    const cssValue = getCssValue();

                    if (cssValue) {// Check null, undefined, ""

                        if (Array.isArray(cssValue) && cssValue.length) {
                            tdElement.classList.add(...cssValue);
                        } else {
                            // Handle backgroundColor override, if strippedRows are enabled and backgroundColor defined, remove box-shadow of bootstrap
                            if (cssAttribute === "backgroundColor" && !this.options.disableStrippedRows) {
                                tdElement.classList.add("shadow-none");
                            }
                            if (cssAttribute === "backgroundColor" && this.options.hierarchyOptions && this.hierarchyHelper.matchHierarchyColumn(cell)) {
                                this.hierarchyHelper.storeCellBackground(cell.row, cssValue);
                            }
                            tdElement.style[cssAttribute] = cssValue;
                        }
                    }
                }
            }
        }
    }

    /**
     * Toggle or collapse a smartTable row 
     * Effective only if hierarchical option is given
     */
    public toggleCollapseOrExpandRow(row: SmartTableRow<ItemData>, type: SmartTableCollapseButtonType): void {

        if (type === "hierarchy") {
            // Spread collapse information in hierarchy
            this.hierarchyHelper.toggleCollapseOrExpandHierarchy(row);
        } else if (type === "child") {
            // Spread collapse child row content
            this.hierarchyHelper.toggleCollapseOrExpandChildRow(row);
        }

        // Set rows to display before refresh (because smart select needs it to determine row count)
        this.updateDisplayableRows();

        this.refresh("toggleRow");

        // Emit event after refresh so smartTable display has already been updated
        this.rowCollapseEvent.emit({
            row: row,
            collapsed: Boolean(row.hierarchyInfo?.collapsed)
        });
    }


    /** Add a button to (collapse | expand) row */
    protected _addCollapseButtonIfNeeded(tdElement: HTMLElement, cell: SmartTableCell<ItemData>): boolean {
        if (this.options.hierarchyOptions && cell.row.hierarchyInfo && this.hierarchyHelper.isCollapsable(cell.row)) {
            const buttonsToCreate: SmartTableCollapseButton[] = [];

            // Create button to expand/collapse hierarchy
            if (this.hierarchyHelper.canShowCollapseOrExpandHierarchyIcon(cell.row)) {
                buttonsToCreate.push({
                    type: "hierarchy",
                    iconName: cell.row.hierarchyInfo.collapsed ? SmartTableCollapseIcons.HIERARCHY_COLLAPSED : SmartTableCollapseIcons.HIERARCHY_EXPANDED
                });
            }

            // Create button to expand/collapse child row content
            if (this.hierarchyHelper.canShowCollapseOrExpandChildIcon(cell.row)) {
                buttonsToCreate.push({
                    type: "child",
                    iconName: cell.row.hierarchyInfo.childCollapsed ? SmartTableCollapseIcons.CHILD_COLLAPSED : SmartTableCollapseIcons.CHILD_EXPANDED
                });
            }

            for (const buttonConfig of buttonsToCreate) {
                // Create collapse button
                const collapseButton = new CjsButton({
                    refAttribute: "collapseBtn_" + cell.row.uid,
                    size: toPx(this.options.hierarchyOptions.collapseButtonWidth ?? SMART_TABLE_HIERARCHY_DEFAULT_WIDTH_ICON),
                    iconMode: "only",
                    cssStyle: ButtonStyle.NONE,
                    icon: {
                        name: buttonConfig.iconName
                    }
                }, {
                    elementCssClasses: [COLLAPSE_BTN]
                });

                collapseButton.classList.add(FLEX_CONTAINER_COLUMN, JUSTIFY_CONTENT.CENTER);


                // If section should be displayed and row not collapsed, display sub-section for button
                if (this.options.hierarchyOptions?.displayHierarchySection && buttonConfig.type === "hierarchy" && !cell.row.hierarchyInfo.collapsed) {
                    collapseButton.append(createHtmlElement("div", { cssClass: ["collapse-btn-hierarchy-section"] }));
                }

                // Stop propagation to avoid row selection
                collapseButton.addEventListener("click", e => e.stopImmediatePropagation());
                // Add click event
                collapseButton.clickEvent.on(() => this.toggleCollapseOrExpandRow(cell.row, buttonConfig.type));

                // Add collapse button in first place
                if (tdElement.children.length > 0 && tdElement.children[0]?.lastChild != null) {
                    tdElement.children[0].insertBefore(collapseButton, tdElement.children[0].lastChild);
                }
            }

            return true;

        }
        return false;
    }

    /**
     * Subtract row height or row height and child row height if exists to the available height
     * @param row Row to append in the smart table
     */
    protected _subtractAvailableHeight(row: SmartTableRow<ItemData>): void {
        const rowHeight = row.height ?? 0;
        const rowClientTrHeight = (row.childTr?.clientHeight ?? 0) + rowHeight;
        this._availableHeight -= this.hierarchyHelper.canInsertRowChild(row) ? rowClientTrHeight : rowHeight;
        if (rowHeight > 0) {
            this._availableHeight -= this.rowCssBordersHeight;
        }
    }

    /**
     * Add hierarchy sections, used to add padding, sections display to hierarchical view
     */
    private _addHierarchySection(tdElement: HTMLElement, cell: SmartTableCell<ItemData>, isCollapseBtnAdded: boolean): void {
        const cellHtml = cell?.html;
        const hierarchy = cell?.row.hierarchyInfo;
        if (!cellHtml || !hierarchy) { return; }

        const tdFirstChild = tdElement.firstChild as HTMLElement;
        const trigramWidth = this.options?.hierarchyOptions?.collapseButtonWidth ?? SMART_TABLE_HIERARCHY_DEFAULT_WIDTH_ICON;
        const trigramContainerWidth = toPx(trigramWidth + 1); // 1 = border width
        document.documentElement.style.setProperty("--collapseBtnWidth", toPx(trigramWidth));
        if (hierarchy.depth > 0) {// 0 level rows are handled in "else"

            const parentParameters = this.hierarchyHelper.getBackgroundColorsFromAncestors(cell.row);
            const parentColors = parentParameters.colors;

            // Loop from deepest row to 0
            for (let depth = hierarchy.depth; depth > 0; depth--) {

                const parentColor = parentColors[hierarchy.depth - depth];

                const trigramContainer = createHtmlElement("div", {
                    cssClass: [FLEX_CONTAINER, POSITION.RELATIVE, JUSTIFY_CONTENT.CENTER, H_100, "trigram-container"],
                    style: {
                        width: trigramContainerWidth,
                        minWidth: trigramContainerWidth
                    }
                });

                const isRowDepth = hierarchy.depth === depth;

                const trigram = createHtmlElement("div", { cssClass: ["trigram"] });

                if (this.options.hierarchyOptions?.displayHierarchySection) {

                    // If sections should be displayed for hierarchy
                    if (isRowDepth) {

                        if (!isCollapseBtnAdded) {
                            // Add a left margin if a collapseBtn is added
                            trigramContainer.style.marginRight = toPx(trigramWidth);
                        }
                        if (hierarchy.isLast) {
                            // Add "last" class for last row in depth section
                            trigram.classList.add("last");
                        }

                        if (hierarchy.parentHierarchy) {
                            trigram.classList.add("has-parent");
                        }

                    }

                    // Set trigram color to match parent
                    if (parentColor) {
                        trigram.style.borderLeft = "1px solid " + parentColor;
                        if (this.options.hierarchyOptions.inheritParentColor) {
                            trigramContainer.style.backgroundColor = parentColor;
                        }
                    }

                    // Check if trigram should be added to current cell
                    const isParent = this.hierarchyHelper.parent.get(cell.row)?.hierarchyInfo?.isParent;

                    const isParentDepth0WithoutChild = Boolean(isParent && !this.hierarchyHelper.children.get(cell.row)?.length && hierarchy.depth === 0);

                    const parentAtDepthIsLast = isRowDepth ? false : parentParameters.isLast[hierarchy.depth - depth - 1];

                    if (!isParentDepth0WithoutChild &&
                        !parentAtDepthIsLast) {
                        trigramContainer.append(trigram);
                    }
                } else if (!isCollapseBtnAdded && isRowDepth) {
                    // Add a left margin if a collapseBtn is added
                    trigramContainer.style.marginRight = toPx(trigramWidth);
                    if (parentColor) {
                        trigramContainer.style.backgroundColor = parentColor;
                    }
                } else if (parentColor) {
                    trigramContainer.style.backgroundColor = parentColor;
                }

                // Add trigram container to first child of cell
                tdFirstChild.prepend(trigramContainer);// Append trigram
            }
        } else if (!isCollapseBtnAdded) {
            const trigramContainer = createHtmlElement("div", {
                cssClass: [FLEX_CONTAINER, POSITION.RELATIVE, JUSTIFY_CONTENT.CENTER, H_100],
                style: {
                    width: toPx(trigramWidth)
                }
            });
            tdFirstChild.prepend(trigramContainer);
        }
    }

    /** Add select children */
    private _addSelectChildren(tdElement: HTMLElement, cell: SmartTableCell<ItemData>): void {
        this.hierarchyHelper.createSelectUnselect(cell, tdElement);
    }


    /** If cell correspond to the first column */
    public matchFirstColumn(cell: SmartTableCell<ItemData> | undefined, options?: { excludeCheckboxColumn?: boolean }): boolean {

        const visibleColumnsLength = getVisibleColumnsFromConfiguration(this.options.columns, this.options.columnsConfig).length;
        if (visibleColumnsLength < 0 || cell === undefined) {
            return false;
        }

        if (this.options.columnsConfig) {
            return getFirstColumn(this.options.columns, this.options.columnsConfig, options)?.uid === cell.column.uid;
        }

        // Get first column excluded the checkbox column
        const firstColumn = options?.excludeCheckboxColumn ? this.options.columns.find(c => !isCheckboxColumn(c.uid)) ?? this.options.columns[0] : this.options.columns[0];
        return firstColumn.uid === cell.column.uid;
    }

    //#region  Filter region


    /** Update displayable rows after hierarchy info updates */
    public updateDisplayableRows(): void {
        // Set rows to display before refresh (because smart select needs it to determine row count)
        this._displayableRows = this._tableRows.filter(this.shouldDisplayRow);

        // Update footer number of displayable rows if td cell element exists
        if (this.options.enableFooterInfo?.displayableRowsCount) {
            this.styleHelper.refreshFooterInfoDisplay();
        }
    }


    //#endregion

    //#region Cell building

    /**
     * Build default table cell
     * @param p
     * @param p.cell where to append content
     * @param p.tdElement where to append cell
     * @param p.renderResult result of cell render (or string value from propertyConfigs)
     * @param p.renderCacheData cache of current render of table
     * @param options to customize cell build
     * @param options.noFlex cell-content not flex display
     */
    protected _defaultCellBuilder(
        p: {
            cell: SmartTableCell<ItemData>,
            tdElement: HTMLTableCellElement,
            renderResult: AcceptedHtmlElementContent | null,
            renderCacheData: RenderCacheData | undefined
        },
        options?: {
            noFlex?: boolean
        }
    ): HTMLDivElement {
        const { cell, tdElement, renderResult } = p;

        const cellDiv = this._buildCellContent(cell, options);

        // Check if renderResult is a string
        const isStringRender = typeof renderResult === "string";

        let renderResultString;
        if (isStringRender) {// Use renderResult as cell string value
            renderResultString = renderResult;
        } else {// If not string type, retrieve string value from displayString or cell.value
            renderResultString = cell.column.getDisplayString?.(cell, p.renderCacheData) ?? cell.value;
        }

        // Store cell string value inside cell
        cell.stringValue = String(renderResultString ?? "");

        // If renderCell result is html type, append result inside cell
        if (renderResult != null && !isStringRender) {

            cellDiv.append(renderResult);
            // Add class to add information inside DOM, used by projects top customize style
            cellDiv.classList.add(CELL_CONTENT_HTML);

            // Add content alignment
            this._applyCellAlignment(cell.column, cellDiv, { isHtmlContent: !isStringRender });

        } else {// No renderCell defined or renderCell return string type => value to append in cell is string

            // Wrap cell string render inside a ellipsis span
            const cellTextSpan = createHtmlElement("span", {
                innerHTML: cell.stringValue,
                cssClass: [W_100, TEXT_TRUNCATE, getSmartTableColumnAlignmentClass(cell.column)],
                attributes: {
                    title: DomTools.htmlStringToMultilineText(cell.stringValue)// Add tooltip into cell
                }
            });
            // Add span into cell
            cellDiv.append(cellTextSpan);
            // Add class to add information inside DOM, used by projects to customize style
            cellDiv.classList.add(CELL_CONTENT_STRING);

            // Add content alignment
            this._applyCellAlignment(cell.column, cellDiv, { isHtmlContent: false, content: cellTextSpan });
        }

        // Add classes to td
        if (cell.column.tdCssClasses?.length) {
            tdElement.classList.add(...cell.column.tdCssClasses.filter(Boolean));// filter(Boolean) => remove empty string classes
        }

        // Add cellDiv to td
        tdElement.append(cellDiv);

        return cellDiv;
    }


    /** Build color table cell */
    public colorCellBuilder(cell: SmartTableCell<ItemData>, cellStringValue: string): HTMLElement {

        const colorCell = this._buildCellContent(cell);

        const color = createHtmlElement("div", {
            cssClass: [COLOR_CELL_CLASS],
            style: {
                backgroundColor: cellStringValue
            },
            attributes: {
                title: cellStringValue
            }
        });
        colorCell.append(color);

        // Add content alignment
        this._applyCellAlignment(cell.column, colorCell, { isHtmlContent: true });
        return colorCell;
    }


    /** Build checkbox table cell */
    public checkboxCellBuilder(cell: SmartTableCell<ItemData>, cellStringValue: string): HTMLDivElement {
        const checkboxCell = this._buildCellContent(cell);
        const checkbox = new CjsCheckbox();
        checkbox.setValue(cellStringValue === this.i18n.checkboxTrueStringValue, false);
        checkbox.options.setWithoutRefresh({
            color: "#666666",
            disabled: true
        });
        checkboxCell.append(checkbox);

        // Add content alignment
        this._applyCellAlignment(cell.column, checkboxCell, { isHtmlContent: true });
        return checkboxCell;
    }

    /** Dispose cells and clear  */
    protected _clearCells(): void {

        const cellsByRowAndColumnToDispose = this._cellsByRowAndColumn;
        this._cellsByRowAndColumn = {};
        this._clearCellsCache();

        const disposeCell = this.options.disposeCell;
        if (cellsByRowAndColumnToDispose && disposeCell) {

            // setTimeout => ensure current rendering is finished before disposing cells
            setTimeout(() => {
                for (const rowUid in cellsByRowAndColumnToDispose) {
                    const rowCells = cellsByRowAndColumnToDispose[rowUid];
                    for (const col in rowCells) {
                        const cell = rowCells[col];
                        if (cell) {
                            disposeCell(cell);
                        }
                    }
                }
            }, 1000);
        }
    }

    /** Clear cells records cache */
    protected _clearCellsCache(): void {
        this._cellsByRowCache = undefined;
        this._cellsByColumnCache = undefined;
    }

    /**
     * Fill tdElement from PropertyConfig of column data
     * @param cell cell to build
     * @param tdElement TdElement we want to fill
     */
    protected _fillTdElementFromPropertyConfig(cell: SmartTableCell<ItemData>, tdElement: HTMLTableCellElement, renderCacheData: RenderCacheData | undefined): void {

        const cellBuildParams: {
            cell: SmartTableCell<ItemData>,
            tdElement: HTMLTableCellElement,
            renderResult: AcceptedHtmlElementContent | null,
            renderCacheData: RenderCacheData | undefined
        } = { cell, tdElement, renderResult: null, renderCacheData };

        // TODO export this part in a propertyConfigTypeHandler
        if (cell.column.propertyConfig) {
            const cellStringValue = cell.column.getDisplayString?.(cell) ?? this.cellPropertyConfigTypeHandler(cell) ?? "";
            cellBuildParams.renderResult = cellStringValue;// Value returned will be added into cell
            switch (cell.column.propertyConfig.typeUid) {
                case PropertyConfigType.COLOR:
                    tdElement.append(this.colorCellBuilder(cell, cellStringValue));
                    break;
                case PropertyConfigType.CHECKBOX:
                case PropertyConfigType.SWITCH:
                    tdElement.append(this.checkboxCellBuilder(cell, cellStringValue));
                    break;
                case PropertyConfigType.TEXTAREA: {
                    const cellDiv = this._defaultCellBuilder(cellBuildParams, { noFlex: true });
                    cellDiv.classList.add(OVERFLOW.HIDDEN);
                    // TODO add tooltip
                    break;
                }
                default:
                    this._defaultCellBuilder(cellBuildParams);
                    break;
            }
        } else {
            this._defaultCellBuilder(cellBuildParams);
        }
    }


    /**
     * Returns the displayed string of a given cell
     * @param cell for which we want the displayed label
     */
    public cellPropertyConfigTypeHandler(cell: SmartTableCell<ItemData>): string | undefined {

        const cellValue = cell.value;

        // Empty if no value
        if (cellValue == null || cellValue === "") {
            return undefined;
        }

        const pc = cell.column.propertyConfig;
        // raw if no pc
        if (!pc) {
            const t = typeof cellValue;
            if (t !== "function" && t !== "object") {
                return String(cellValue);
            }
            return undefined;
        }

        // formatted depending on property config type
        switch (pc.typeUid) {
            case PropertyConfigType.DATE:
                return format(cellValue, DEFAULT_DATE_FORMAT);
            case PropertyConfigType.FINISH_DATE:
                return formatFinishDate(cellValue, DEFAULT_DATE_FORMAT);
            case PropertyConfigType.DATETIME:
                return format(cellValue, DEFAULT_DATETIME_FORMAT);
            case PropertyConfigType.DATE_INTERVAL:
                return `${format(cellValue.start, DEFAULT_DATE_FORMAT)} - ${formatFinishDate(cellValue.finish, DEFAULT_DATE_FORMAT)}`;
            case PropertyConfigType.TIME_IN_SECONDS:
                return formatSecondsHHMM(cellValue);
            case PropertyConfigType.TIME:
            case PropertyConfigType.DURATION:
                return (pc.options as PropertyOptionsDuration)?.pickerOptions?.useSeconds ? formatSecondsHHMM(cellValue) : formatMinutesHHMM(cellValue);
            case PropertyConfigType.LISTSELECT: {
                return this.handleListSelect(pc as PropertyConfig<PropertyConfigType.LISTSELECT, PropertyOptionsList>, cell.value) ?? "";
            }
            case PropertyConfigType.REFTABLE:
            case PropertyConfigType.REFLIST: {
                return this.handleRefList(pc, cell.value) ?? "";
            }
            case PropertyConfigType.SWITCH:
            case PropertyConfigType.CHECKBOX: {
                // True: Yes/Oui - False: No/Non
                return cell.value === true ? this.i18n.checkboxTrueStringValue : this.i18n.checkboxFalseStringValue;
            }
            default:
                return String(cellValue);
        }
    }

    /**
     * Returns data for export based on pc types
     * @param cell for which we want the displayed label
     */
    public cellExportPropertyConfigTypeHandler(cell: SmartTableCell<ItemData>): string | number | Date | undefined {

        const cellValue = cell.value;

        // Empty if no value
        if (cellValue == null || cellValue === "") {
            return undefined;
        }

        const pc = cell.column.propertyConfig;
        // raw if no pc
        if (!pc) {
            const t = typeof cellValue;
            if (t !== "function" && t !== "object") {
                return String(cellValue);
            }
            return undefined;
        }

        // formatted depending on property config type
        switch (pc.typeUid) {
            case PropertyConfigType.AGE:
            case PropertyConfigType.NUMBER:
                return cell.value;
            case PropertyConfigType.DATE:
            case PropertyConfigType.DATETIME:
                // ExcelJs assume all datetime is encode in UTC
                return toJsDate(toDate(cellValue, UTC).add({ minute: toDate(cell.value, getDefaultDateLocale()).offset }).uts);
            case PropertyConfigType.FINISH_DATE:
                // ExcelJs assume all datetime is encode in UTC. We subtract 1 day because it's finishDate (same logic as in DateEditor)
                return toJsDate(toDate(cellValue, UTC).add({ day: -1, minute: toDate(cell.value, getDefaultDateLocale()).offset }).uts);
            case PropertyConfigType.TIME:
                return formatMinutesHHMM(cellValue);
            case PropertyConfigType.TIME_IN_SECONDS:
                return formatSecondsHHMM(cellValue);
            case PropertyConfigType.DURATION:
                return this.handleDurationEditor(pc as PropertyConfig<PropertyConfigType.DURATION, PropertyOptionsList>, cellValue);
            case PropertyConfigType.LISTSELECT:
                return this.handleListSelect(pc as PropertyConfig<PropertyConfigType.LISTSELECT, PropertyOptionsList>, cell.value) ?? "";
            case PropertyConfigType.REFLIST:
            case PropertyConfigType.REFTABLE:
                return this.handleRefList(pc, cell.value) ?? "";
            case PropertyConfigType.SWITCH:
            case PropertyConfigType.CHECKBOX: {
                // True: Yes/Oui - False: No/Non
                return cell.value === true ? this.i18n.checkboxTrueStringValue : this.i18n.checkboxFalseStringValue;
            }
            default:
                return String(cellValue);
        }
    }

    //#endregion

    //#region Scrollbar handling

    /** Build a cell content */
    protected _buildCellContent(cell: SmartTableCell<ItemData>, options?: { noFlex?: boolean }): HTMLDivElement {

        const cellContent = createHtmlElement("div", {
            cssClass: this._getCellCssClasses(cell, options)
        });
        return cellContent;
    }

    /** Apply content alignment to cell */
    protected _applyCellAlignment(column: SmartTableColumn<ItemData>, container: HTMLElement, options: { isHtmlContent: boolean, content?: HTMLElement }): void {

        const alignmentClass = getSmartTableColumnAlignmentClass(column, options.isHtmlContent);
        const paddingClass = getSmartTableColumnAlignmentPaddingClass(column, this);

        container.classList.add(paddingClass);
        (options.content ?? container).classList.add(alignmentClass);
    }

    /** Get cssClasses for cell */
    protected _getCellCssClasses(cell: SmartTableCell<ItemData>, options?: { noFlex?: boolean }): string[] {

        const colClasses = [CELL_CONTENT, ...cell.column.cellCssClasses ?? []];
        if (this.hierarchyHelper.matchHierarchyColumn(cell) && cell.row.hierarchyInfo?.depth != null) {
            colClasses.push(CELL_HIERARCHICAL);
        } else if (!options?.noFlex) {
            colClasses.push(CELL_FLEX);
        }
        return colClasses;
    }

    //#endregion

    //#region  Data smartTable 

    /** Handle durationEditor export data */
    public handleDurationEditor(pc: PropertyConfig<PropertyConfigType.DURATION, PropertyOptionsDuration>, cellValue: anyReallyAny): string | number | undefined {
        if (pc.options?.pickerOptions?.useSeconds) {
            return formatSecondsHHMM(cellValue);
        } else if (pc.options?.pickerOptions?.useDays) {
            return cellValue;
        } else {
            return formatMinutesHHMM(cellValue);
        }
    }

    /**
     * Convert value or list of selected values to a display string
     * @param pc PropertyConfig
     * @param value Value of the data 
     * @returns Display string of the value or undefined
     */
    public handleListSelect(pc: PropertyConfig<PropertyConfigType.LISTSELECT, PropertyOptionsList>, value: anyReallyAny | anyReallyAny[]): string | undefined {
        let valueAsString: string | undefined = undefined;
        // Check if options.itemList is DataSourceUID
        if (pc.options && typeof pc.options.itemList === "string") {
            const dataSourceUID = pc.options.itemList;
            const dataSourceService = injector.get<DefaultDataSourceService>(DATA_SOURCE_SERVICE);
            const dataSource = dataSourceService.getDataSource<string, string, unknown>(dataSourceUID);
            if (dataSource) {
                valueAsString = wrapAsArray(value).map(val => dataSource.getDisplayString(val)).join(", ");
            } else {
                this._log?.error("DataSource '" + dataSourceUID + "' not found for propertyConfig " + pc.propertyName);
                valueAsString = undefined;
            }
        } else {
            valueAsString = wrapAsArray(value).join(", ");
        }
        return valueAsString;
    }

    /**
     * Handle listSelect case of propertyConfig
     * @param pc PropertyConfig
     * @param values Values of the data 
     * @returns datasource result or undefined
     */
    public handleRefList(pc: PropertyConfig, values: anyReallyAny[]): string | undefined {
        const options = pc.options as PropertyOptionsList | undefined;
        let resultDataSource: string | undefined = undefined;
        // Check if options.itemList is DataSourceUID
        if (options && typeof options.itemList === "string") {
            if (values == null) { return undefined; }
            const dataSourceUID = options.itemList;
            const dataSourceService = injector.get<DefaultDataSourceService>(DATA_SOURCE_SERVICE);
            const dataSource = dataSourceService.getDataSource<string, string, unknown>(dataSourceUID);
            if (dataSource) {
                resultDataSource = values.map(value => dataSource.getDisplayString(value)).join(", ");
            } else {
                this._log?.error("DataSource '" + dataSourceUID + "' not found for propertyConfig " + pc.propertyName);
                resultDataSource = "";
            }
        } else {
            // TODO  Remove getVOFValue when options.itemList won't be a ValueOrFunction<anyReallyAny[]> anymore 
            const tmp = getVOFValue(options?.itemList) as anyReallyAny[];
            resultDataSource = tmp.join(", ");
        }
        return resultDataSource;
    }

    //#endregion

    //#region StickyRow resize

    // TODO protected _stickyRowResizeInteractionInfos=undefined;
    /** Mouse down event of sticky row for resize */
    protected _stickyRowResizerEventMouseDown!: MouseEvent;

    /** Sticky row to resize (by the resizerRow) */
    protected _stickyRowToResize: SmartTableRow<ItemData> | undefined;

    /** Row resizer for current resize interaction */
    protected _stickyRowResizer: HTMLElement | undefined;

    /** Height of row to resize before resizing */
    protected _rowToResizeInitialHeight = 0;


    /**
     * When mouse down, add "onStickyRowResizerMouseMove" and "onStickyRowResizerMouseUp" events
     */
    public onStickyRowResizerMouseDown = (eventMouseDown: MouseEvent): void => {

        // ! this function is also used by the timeline resizer div (resizer can be a tr or a div)

        // Keep mouseDown event
        this._stickyRowResizerEventMouseDown = eventMouseDown;

        // Get resizer (parent of tdElement where mouseDown trigger)
        this._stickyRowResizer = (eventMouseDown.target as HTMLElement) as HTMLElement;
        if (this._stickyRowResizer.tagName.toUpperCase() === "TD" && this._stickyRowResizer.parentElement) {
            this._stickyRowResizer = this._stickyRowResizer.parentElement;// get the tr
        }
        this._stickyRowResizer.classList.add(RESIZED_ROW_CLASS);

        // prevent hover during resize
        this.table.classList.remove(TABLE_HOVER);

        // Retrieve row in stickyRows list
        const rowUid = this._stickyRowResizer.getAttribute(ROW_UID_ATTR);
        this._stickyRowToResize = this._stickyTableRows.find(sr => sr.uid === rowUid);

        // Store this row height
        this._rowToResizeInitialHeight = this._stickyRowToResize?.tr?.clientHeight ?? 0;

        // Add event listeners
        document.addEventListener("mouseup", this.onStickyRowResizerMouseUp);
        document.addEventListener("mousemove", this.onStickyRowResizerMouseMove);
    };

    /** 
     * Change sticky row height when mouse is moved
     */
    public onStickyRowResizerMouseMove = (e: MouseEvent): void => {
        const rowToResize = this._stickyRowToResize;

        if (!this._canResizeStickyRow || !rowToResize?.tr || rowToResize.sticky == null) {
            return;
        }
        const stickyType = rowToResize.sticky.stickyType;
        const upsideDown = stickyType === "header" ? 1 : -1;

        const staticRowResizeDebounce = 10;// ms

        this._canResizeStickyRow = false;
        setTimeout(() => { this._canResizeStickyRow = true; }, staticRowResizeDebounce);

        const heightOffset = e.clientY - this._stickyRowResizerEventMouseDown.clientY;
        let newHeight = this._rowToResizeInitialHeight + upsideDown * heightOffset;

        const totalStickyRowsHeight = stickyType === "header" ?
            this._totalStickyHeaderRowsHeight :
            this._totalStickyFooterRowsHeight;

        // We don't want the resizer to disappear. We want at least 1 other row to be displayed
        if (rowToResize.sticky.height && newHeight > rowToResize.sticky.height) {
            const growing = newHeight - rowToResize.sticky.height;
            const standardRowHeight = this.getRowHeight();
            if (totalStickyRowsHeight > this._bodyVisibleHeight) {
                // the last sticky row resizer was already not displayed before this interaction... => there is nothing we can do
            } else if (totalStickyRowsHeight + growing + standardRowHeight > this._bodyVisibleHeight) {
                // if growing row and already at bottom, we limit
                const computedNewHeight = rowToResize.sticky.height + this._bodyVisibleHeight - totalStickyRowsHeight - standardRowHeight;
                if (computedNewHeight >= rowToResize.sticky.height) {
                    newHeight = computedNewHeight;
                } else {
                    // we should be growing... there is a computation problem => we keep the old value
                    newHeight = rowToResize.sticky.height;
                }
            }
        }

        // Handle min height of stickyRow
        newHeight = this._adjustStickyRowHeightIfNeeded(newHeight, rowToResize);

        // Resize the stick row
        rowToResize.sticky.height = newHeight;
        rowToResize.tr.style.height = toPx(newHeight);
        rowToResize.height = rowToResize.tr.clientHeight;

        // Render (needed to compute clientHeight for dispatch event below)
        this.render("resizeRowIncremental");

        // Dispatch event
        this.onResizeStickyContainer.emit({
            rowIndex: rowToResize.globalIndex,
            rowHeight: rowToResize.tr.clientHeight,
            stickyType
        });
    };

    /**
     * When mouse up, remove "mouseMove" and "mouseUp" events
     */
    public onStickyRowResizerMouseUp = (): void => {

        // Reset values
        this._stickyRowResizer?.classList.remove(RESIZED_ROW_CLASS);
        this._rowToResizeInitialHeight = 0;
        this._stickyRowToResize = undefined;
        this._stickyRowResizer = undefined;
        document.removeEventListener("mouseup", this.onStickyRowResizerMouseUp);
        document.removeEventListener("mousemove", this.onStickyRowResizerMouseMove);
        this.render("resizeRowFinish");

        // Restore hover
        toggleClass(this.table, !this.options.disableHover, TABLE_HOVER);
    };

    //#endregion StickyRow resize


    //#region Scroll shadows

    /** Top shadowDiv element */
    private _scrollShadowTop?: HTMLElement;

    /** Bottom shadowDiv element */
    private _scrollShadowBottom?: HTMLElement;

    /**
     * Append _shadowTop and _shadowBottom if needed
     */
    protected _refreshScrollShadowsVisibility(): void {

        if (this._scrollShadowTop == null && this.options.enableScrollShadows) {
            /** Top shadowDiv element */
            this._scrollShadowTop = createHtmlElement("div", { cssClass: SCROLL_SHADOW_TOP_CLASS });

            /** Bottom shadowDiv element */
            this._scrollShadowBottom = createHtmlElement("div", { cssClass: SCROLL_SHADOW_BOTTOM_CLASS });
            this.table.append(this._scrollShadowTop, this._scrollShadowBottom);
        } else if (this._scrollShadowTop != null && !this.options.enableScrollShadows) {
            this._scrollShadowTop?.remove();
            this._scrollShadowBottom?.remove();
            this._scrollShadowTop = undefined;
            this._scrollShadowBottom = undefined;
        }
    }

    /** Refresh scroll shadow */
    public _refreshScrollShadows(): void {
        if (this.options.enableScrollShadows && this._scrollShadowTop && this._scrollShadowBottom) {// Show shadow divs when scrolling
            const body = this.components.tableBody;

            const shadowDivHeight = 20;
            this._scrollShadowTop.style.top = body.offsetTop + this._totalStickyHeaderRowsHeight + "px";
            this._scrollShadowBottom.style.top = body.offsetTop + this._bodyVisibleHeight - shadowDivHeight + "px";

            toggleVisible(this._scrollShadowTop, this.options.firstDisplayedItemIndex > 0, Display.BLOCK);
            toggleVisible(this._scrollShadowBottom, !this.isLastRowDisplayed(), Display.BLOCK);
        } else {
            if (this._scrollShadowTop) {
                setDisplay(this._scrollShadowTop, Display.NONE);
            }
            if (this._scrollShadowBottom) {
                setDisplay(this._scrollShadowBottom, Display.NONE);
            }
        }
    }

    //#endregion


    //#region selection


    /**
     * Get selectedRows
     */
    public getSelectedRows(): SmartTableRow<ItemData>[] {
        return this._tableRows.filter(r => this._selectionModel.isSelected(r.uid));
    }

    /**
     * Get selected items.
     * Filters rows with null or undefined data.
     * @returns ItemData[]
     */
    public getSelectedItems(): ItemData[] {
        const result: ItemData[] = [];
        for (const row of this._tableRows) {
            if (!this._selectionModel.isSelected(row.uid)) {
                // Row is not selected
                continue;
            }
            const rowData = this.getRowItemData(row);
            if (rowData == null) {
                // No data associated to the row
                continue;
            }
            result.push(rowData);
        }
        return result;
    }

    /**
     * Returns list of items regarding applied filters.
     * Filters rows with null or undefined data.
     * @returns ItemData[]
     */
    public getDisplayableItems(): ItemData[] {
        const result: ItemData[] = [];
        for (const row of this.displayableRows) {
            const rowData = this.getRowItemData(row);
            if (rowData == null) {
                // No data associated to the row
                continue;
            }
            result.push(rowData);
        }
        return result;
    }

    /**
     * Returns list of items for sticky rows
     * @returns ItemData[] sticky items
     */
    public getStickyItems(): ItemData[] {
        const result: ItemData[] = [];
        for (const row of this.stickyTableRows) {
            const rowData = this.getRowItemData(row);
            if (rowData == null) {
                // No data associated to the row
                continue;
            }
            result.push(rowData);
        }
        return result;
    }

    /**
     * Get the smartList selection model
     * @returns smartList's selection model
     */
    public getSelectionModel(): SmartTableSelectionModel<ItemData> {
        return this._selectionModel;
    }

    /**
     * Get rows data of the smartTable
     */
    public getRowsData(): ItemData[] {
        const rowsData = [];
        for (const row of this.tableRows) {
            const value = this.getRowItemData(row);
            // Avoid null coming from the typings of getRowItemData() because row.data as a ValueOrFunction can return undefined
            // TODO this can break indexes between rows and getRowsData()
            if (value === null) { continue; }
            rowsData.push(value);
        }
        return rowsData;
    }

    /**
     * Select rows with their uid
     * * Will replace current selection, for 'add' behavior, set 'options.add' to true
     * @param rowUids new selection: indexes to select
     * @param options for selection behavior
     * @param options.interval If false: selection will be set with all rows in new selection, if true: selection will be set as an interval (from first row to last row given). 
     * @param options.add if true, new selection is added to current selection, if false, new selection will replace current selection
     * @param options.preventScroll if true, table will not scroll to new selection
     * @param options.doNotDispatchChangeEvent if true, change event will not be dispatched
     * @param options.doNotRefreshIfScroll if true, scrollToSelectedRow will not cause a refresh (valid only if preventScroll is false or undefined)
     */
    public selectRows(rowUids: (RowUid | RowUid[]), options?: { interval?: boolean, add?: boolean, preventScroll?: boolean, doNotDispatchChangeEvent?: boolean, doNotRefreshIfScroll?: boolean }): void {
        // If empty selection index array: clear selection model, refresh it, reset scroll and return
        if (rowUids.length === 0) {
            this._selectionModel.clear({ doNotDispatchEvent: options?.doNotDispatchChangeEvent });
            return;
        }

        const setOrAddSelectMethod = options?.add ? this._selectionModel.select.bind(this._selectionModel) : this._selectionModel.setSelection.bind(this._selectionModel);

        if (options?.interval) {
            // Select range between first and last row
            this._selectionModel.leadSelectionRow = rowUids[0];
            setOrAddSelectMethod(rowUids, { isCtrl: false, isShift: true, doNotToggleSelection: true, doNotDispatchChangeEvent: options?.doNotDispatchChangeEvent });
        } else {
            setOrAddSelectMethod(rowUids, { isCtrl: true, isShift: false, doNotToggleSelection: true, doNotDispatchChangeEvent: options?.doNotDispatchChangeEvent });
        }

        if (!options?.preventScroll) {
            this.scrollToSelectedRow({ doNotRefresh: options?.doNotRefreshIfScroll });
        }
    }

    /** Shortcut to add displayed rows to the current selection */
    public selectDisplayableRows(options?: { interval?: boolean, add?: boolean, preventScroll?: boolean, doNotDispatchChangeEvent?: boolean }): void {
        const rowUids = this.displayableRows.map(row => row.uid);
        this.selectRows(rowUids, options);
    }

    /**
     * Listener callback when user press a keyboard key, handle "ctrl + A" keys pressed
     */
    private _selectAllFromKeyboard: EventListenerObject = {
        handleEvent: (e: KeyboardEvent) => {
            // Handle only ctrl+A keys
            if (e.key !== KeyboardInput.A || !e.ctrlKey) { return; }

            // Don't select all elements in page
            e.preventDefault();

            const activeElement = document.activeElement;
            if (!activeElement || activeElement !== this) { return; }

            // Don't apply select all when single selection is enable
            if (this._selectionModel.type === SELECTION_TYPE.SINGLE) { return; }

            // Retrieve all rows selectable of smartTable
            const allRowsSelectable = this.displayableRows.filter(this.isSelectableRow.bind(this)).map(r => r.uid);

            // Select all rows (set)
            this._selectionModel.setSelection(allRowsSelectable, { isShift: true, isCtrl: true, doNotToggleSelection: true });
        }
    };

    /**
     * Scroll table to first selectedRow
     * If possible, will center selectedRow inside table body
     */
    public scrollToSelectedRow(p?: { doNotRefresh?: boolean, reason?: string }): void {

        const firstSelectedRow = this._selectionModel.getSelection()[0];
        if (!firstSelectedRow) { return; } // No rows selected, can't scroll

        // Find corresponding displayable row index
        const firstIndex = this.displayableRows.findIndex(r => r.uid === firstSelectedRow) ?? 0;
        if (firstIndex == null) { return; }

        // If table isn't smart, all rows are rendered, scroll to firstSelectedRow, set it into center of scrollView
        if (!this.isSmart() && isScrollable(this.components.smartTableContainer)) {// Check if smartTableContainer is scrollable before scrolling
            const rowToScroll = this.displayableRows[firstIndex - 1];
            const rowToScrollTr = rowToScroll?.tr;
            if (rowToScrollTr) {
                rowToScrollTr.scrollIntoView({ block: "center" });
                this.scrollEvent.emit({ index: this.options.firstDisplayedItemIndex });
            }
            return;
        }

        // Compute new scroll value
        const displayedRowsCount = this.displayedRows.length;
        const allDisplayedRowsCount = this.stickyTableRows.length + displayedRowsCount;

        // Get current index displayed in table
        const currentDisplayedIndex = this.options.firstDisplayedItemIndex;

        // Compute scroll from currentDisplayedIndex and firstIndex to display
        let newSmartTableIndex = currentDisplayedIndex;

        if (firstIndex <= this.displayableRows.length && firstIndex + allDisplayedRowsCount > this.displayableRows.length) {
            // Avoid scroll if firstIndex is close than last row
            newSmartTableIndex = this.displayableRows.length - allDisplayedRowsCount;
        } else if (firstIndex < currentDisplayedIndex) {
            // Scroll top
            newSmartTableIndex = firstIndex;
        } else if (Math.abs(currentDisplayedIndex - firstIndex) >= allDisplayedRowsCount) {
            // Scroll down

            // We center first row selected if possible
            const centerFirstIndex = firstIndex - Math.floor(displayedRowsCount / 2);
            newSmartTableIndex = centerFirstIndex < 0 ? 0 : centerFirstIndex;
        }

        // Refresh scroll
        this.options.setWithoutRefresh({ firstDisplayedItemIndex: newSmartTableIndex });

        // Refresh smartTable
        if (!p?.doNotRefresh) {
            this.refresh(p?.reason ?? "ScrollToSelectedRow");
            this.scrollEvent.emit({ index: this.options.firstDisplayedItemIndex });
        }
    }

    /** Select the next row than current selected one */
    public selectNextRow(): void {
        const selection = this._selectionModel.getSelection();
        const lastRowUidSelected: RowUid | undefined = selection[selection.length - 1];

        const lastRowUidSelectedIndex = lastRowUidSelected != null ? this.displayableRows.findIndex(r => r.uid === lastRowUidSelected) : undefined;

        let nextIndex: number | undefined;
        if (lastRowUidSelectedIndex != null) {
            // Retrieve row index after current selected 
            nextIndex = lastRowUidSelectedIndex + 1;
        } else {
            // if no row previously selected, select first one
            if (this.displayableRows.length) {
                nextIndex = 0;
            } else {
                return;
            }
        }

        // Handle negative index
        if (nextIndex == null || nextIndex < 0 || nextIndex > this.displayableRows.length - 1) { nextIndex = 0; }

        // Get row for this index into displayable rows
        const nextRow = this.displayableRows[nextIndex];

        if (nextRow) {
            // Select row
            this.selectRows(nextRow.uid);
        }
    }
    /** Select the previous row than current selected one */
    public selectPreviousRow(): void {
        const selection = this._selectionModel.getSelection();
        const lastRowUidSelected: RowUid | undefined = selection[selection.length - 1];
        const lastRowUidSelectedIndex = lastRowUidSelected != null ? this.displayableRows.findIndex(r => r.uid === lastRowUidSelected) : undefined;

        let previousIndex: number | undefined;
        if (lastRowUidSelectedIndex != null) {
            // Retrieve row index after current selected 
            previousIndex = lastRowUidSelectedIndex - 1;
        } else {
            // if no row previously selected, select first one
            if (this.displayableRows.length) {
                previousIndex = this.displayableRows.length - 1;
            } else {
                return;
            }
        }

        // Handle negative index
        if (previousIndex == null || previousIndex < 0) {
            // Get index of last row
            previousIndex = this.displayableRows.length - 1;
        }

        // Get row for this index into displayable rows
        const previousRow = this.displayableRows[previousIndex];

        if (previousRow) {
            // Select row
            this.selectRows(previousRow.uid);
        }
    }

    /**
     * Select items inside smartTable
     * @param items to select
     * @param equalComparator compare items with value comparator (ex: id, uid,...). Default: reference compare
     * @param options for selection behavior
     * @param options.interval If false: selection will be set with all rows in new selection, if true: selection will be set as an interval (from first row to last row given). 
     * @param options.add if true, new selection is added to current selection, if false, new selection will replace current selection
     * @param options.preventScroll if false, table will not scroll to new selection
     * @param options.doNotDispatchChangeEvent if true, change event will not be dispatched after selection change
     * @see selectRows
     * 
     * Warning : If you have a lot of data, you should prefer to set getRowUid and use @see selectRows directly
     */
    public selectItems(
        items: ItemData | ItemData[],
        equalComparator?: (item1: ItemData, item2: ItemData) => boolean,
        options?: { interval?: boolean, add?: boolean, preventScroll?: boolean, doNotDispatchChangeEvent?: boolean }
    ): void {
        const itemsArr = wrapAsArray(items);

        // Row global indexes linked to item data to select
        const rowsToSelect: RowUid[] = [];

        // Loop inside items first to keep selection order
        for (const item of itemsArr) {
            // Loop inside all rows
            for (const r of this.tableRows) {
                // Get row data to compare
                const rowData = this.getRowItemData(r);
                if (rowData == null) { continue; }

                const isEqual = equalComparator?.(rowData, item) ?? item === rowData;
                if (isEqual) {
                    rowsToSelect.push(r.uid);
                    break;// Item is equal, useless to continue to search for another row
                }
            }

            // if all items linked uid are found, useless to continue search, end loop
            if (rowsToSelect.length === itemsArr.length) { break; }
        }

        // Select rows
        this.selectRows(rowsToSelect, options);
    }

    /**
     * Returns true if row is enabled for the selection
     * @param row Row to check
     */
    public isSelectableRow(row: SmartTableRow<ItemData>): boolean {
        if (!row) { return false; }

        const data = this.getRowItemData(row);
        const isSelectableOption = this.options.selection?.isRowSelectable;
        // Check if isRowSelectable option is defined, if return false for data, row isn't selectable
        if (data != null && isSelectableOption && !isSelectableOption(row, data)) {
            return false;
        }

        switch (typeof this.options.selection?.toggleSelectionOnClick) {
            case "boolean":
                return this.options.selection.toggleSelectionOnClick;
            case "function": {
                return data != null ? this.options.selection.toggleSelectionOnClick(data) : false;
            }
            default:
                return true;
        }
    }

    //#endregion


    /** @returns true if table is smart */
    public isSmart(): boolean {
        if (this.options.smart === false) {
            return false;
        }
        if (this.options.scrollBar === "standard") {
            return false;
        }
        if (this.options.parentContainerHasNoHeightConstraint) {
            return false;
        }
        return true;
    }
}
